import { Component, ChangeDetectorRef, ElementRef, HostListener, NgZone, OnDestroy, OnInit, AfterViewInit, ViewChild } from '@angular/core';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatSelect } from '@angular/material/select';
import { MatSlideToggleChange } from '@angular/material/slide-toggle';
import { MatTooltip } from '@angular/material/tooltip';
import { Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { Subscription, interval, forkJoin, of } from 'rxjs';
import { switchMap } from 'rxjs/operators';
import { AuthService } from 'src/app/auth/auth.service';
import { DialogService } from 'src/app/dialog/dialog.service';
import { DirectionService } from 'src/app/direction.service';
import { ClustersService } from 'src/app/main-section/choose-branch/clusters.service';
import { UsersService } from 'src/app/super-management/user/users.service';
import {
  ExhibitionComment,
  WallApiService,
  WallPublishItemPayload,
  WallPublishedItem,
  WallUnsharedPrintedItem,
  isWallPlaceholderThumb,
  toPublicWallThumb,
  resolveWallItemThumb,
  wallProfileImageCandidates,
  isWallDesktopViewport,
  isWallImagePanoramic,
  wallDisplayedSize,
  wallFocusMediaEl,
  wallFocusPinPanelToBox,
  wallFocusNaturalPanelHeight,
  wallFocusNatFromPanelImg,
  wallFocusPlayCloseToSlot,
  wallFocusPlayOpenFromSlot,
  wallFocusClearMediaFlipStyles,
  hydrateWallItemImageSizes,
  filterWallItemsWithLoadableThumbs,
  wallDisplayedAspectChanged,
  wallScrollParent,
  bindWallScrollCollapse,
} from '../wall-api.service';
import {
  GalleryLayoutRow,
  GalleryRowsLayoutService,
  GALLERY_ROWS_TUNABLES,
  WallSkeletonWork,
  isWallSkeletonWork,
  makeWallSkeletonWorks,
  wallSkeletonCount,
} from '../gallery-rows-layout.service';
import { WallSessionMeta, WallSessionService } from '../wall-session.service';
import { wallCommentsLoadReservePx } from '../wall-comments-visible';

/** Natural pixel size — used to size media box after CSS rotation */
export interface WallImageFitState {
  natW?: number;
  natH?: number;
  imgLoaded?: boolean;
}

export interface WallFileItem extends WallImageFitState {
  id: string;
  fileId: string;
  orderId: string;
  fileName: string;
  title: string;
  description: string;
  thumbUrl: string;
  included: boolean;
  /** 1-based page number */
  page: number;
  /** Total pages in the source file */
  pageCount: number;
  /** Clockwise rotation for wall display */
  rotation: 0 | 90 | 180 | 270;
  /** Publish to personal wall but exclude from public exhibition */
  hiddenFromAudience: boolean;
}

/** Published item + local edit draft (saved only on explicit save) */
export interface WallPublishedViewItem extends WallPublishedItem, WallImageFitState {
  commentsOpen: boolean;
  commentsMounted: boolean;
  commentsLoading: boolean;
  comments: ExhibitionComment[];
  draftComment: string;
  liking: boolean;
  commenting: boolean;
  likeCount: number;
  commentCount: number;
  commenterCount: number;
  likedByMe: boolean;
  recentLikers: string[];
  recentCommenters: string[];
  likers: string[];
  likersExpanded: boolean;
  editingTitle: boolean;
  editingDescription: boolean;
  savingTitle: boolean;
  savingDescription: boolean;
  deleting: boolean;
  hiding: boolean;
  draftTitle: string;
  draftDescription: string;
  draftRotation: 0 | 90 | 180 | 270;
  baselineTitle: string;
  baselineDescription: string;
  baselineRotation: 0 | 90 | 180 | 270;
  hiddenFromAudience: boolean;
  workTitleExpanded?: boolean;
  workDescExpanded?: boolean;
  workTitleOverflow?: boolean;
  workDescOverflow?: boolean;
  /** Local-only card opened from unshared tab before publish API runs. */
  pendingShareFromUnshared?: boolean;
  sourceUnsharedId?: string;
}

/** Printed page not yet shared to the wall (infinite-scroll feed). */
export interface WallUnsharedViewItem
  extends WallUnsharedPrintedItem,
    WallImageFitState {
  sharing?: boolean;
}

@Component({
  selector: 'app-personal-wall',
  templateUrl: './personal-wall.component.html',
  styleUrls: ['./personal-wall.component.css'],
  host: {
    class: 'fill-screen personal-wall-host',
    '[class.personal-wall-host-dark]': 'isDarkMode',
    '[class.personal-wall-host-focus]': '!!commentsTargetId',
  },
})
export class PersonalWallComponent implements OnInit, AfterViewInit, OnDestroy {
  isRTL = true;
  isDarkMode = false;
  /** Shared / unshared / profile tabs */
  worksTab: 'published' | 'unshared' | 'profile' = 'published';
  /** Stable tab badges — only change on server commit or intentional local actions. */
  publishedTabCount = 0;
  unsharedTabCount = 0;
  isLoading = true;
  isPublishing = false;
  /** Draft section collapsing after successful publish */
  isDraftCollapsing = false;
  /** Soft green glow on works that were just published */
  private justAddedIds = new Set<string>();
  private queuedJustAddedIds: string[] = [];
  private pendingHighlightKeys = new Set<string>();
  /** Skip waiting for draft collapse when highlighting (e.g. share from unshared). */
  private justAddedPreferImmediate = false;
  /** After share from unshared: open title/desc edit + scroll into view */
  private pendingRevealKeys = new Set<string>();
  /** Unshared source kept while a pending-share focus card is open (restore on cancel). */
  private pendingShareSourceById = new Map<string, WallUnsharedViewItem>();
  /** One-shot open from my-orders share button (same UX as unshared share). */
  private pendingDirectShareConsumed = false;
  private justAddedClearTimer: ReturnType<typeof setTimeout> | null = null;
  private revealScrollRaf: number | null = null;
  isLoadingPublished = true;
  isScrolledDown = false;
  private scrollListener: (() => void) | null = null;
  userName = '';
  userProfileImg: string | null = null;
  private profileImgCandidates: string[] = [];
  commentsTargetId: string | null = null;
  /** Focus dialog mode: social (comments) or edit (work tools). */
  publishedFocusMode: 'social' | 'edit' | null = null;
  /** True after profile fetch applied displayName (not auth email/username). */
  private profileNameLoaded = false;
  baselineName = '';
  draftName = '';
  editingName = false;
  userEmail = '';
  userPhone: string | number | null = null;

  /** Wall-level "about you" (saved on walls.about) */
  baselineAbout = '';
  draftAbout = '';
  editingAbout = false;
  savingProfile = false;

  /** Professional status toggles (saved on wall) */
  lookingForWork = false;
  offeringHelp = false;
  allowContact = true;
  savingStatus = false;
  showContactLockedTip = false;
  private contactTipHideTimer: ReturnType<typeof setTimeout> | null = null;

  /** Academic institution (branch serial_name on wall) */
  academySerialName = '';
  academyOptions: { serialName: string; labelKey: string }[] = [];
  savingAcademy = false;
  editingAcademy = false;
  private academyOptionsLoaded = false;
  /** Once per page load: assign academy from order-share session if wall has none */
  private academyFromSessionEnsured = false;
  /** UI-only default (not saved) while the wall has no academy and no shared works */
  private academyIsSuggested = false;
  private wallLoaded = false;
  private wallHasItems = false;
  private recentPrintingServices: string[] | null = null;
  private recentBranches: string[] | null = null;

  @ViewChild('academySelect') academySelect?: MatSelect;
  @ViewChild('publishedCollage') publishedCollageRef?: ElementRef<HTMLElement>;
  @ViewChild('publishedSentinel') publishedSentinelRef?: ElementRef<HTMLElement>;
  @ViewChild('unsharedCollage') unsharedCollageRef?: ElementRef<HTMLElement>;
  @ViewChild('unsharedSentinel') unsharedSentinelRef?: ElementRef<HTMLElement>;

  /** Draft works from current order-summary session (to publish) */
  wallFiles: WallFileItem[] = [];
  /** Already published wall items from server (with local edit drafts) */
  publishedItems: WallPublishedViewItem[] = [];
  publishedLayoutRows: GalleryLayoutRow<WallPublishedViewItem | WallSkeletonWork>[] = [];
  publishedLayoutTotalHeight = 0;
  publishedLayoutGap: number = GALLERY_ROWS_TUNABLES.gapDesktop;
  publishedSkeletonSlots: WallSkeletonWork[] = [];
  /** Progressive reveal for shared works (scroll to load more). */
  readonly publishedPageSize = 10;
  publishedVisibleCount = 0;

  /** Unshared printed works (infinite scroll) */
  readonly unsharedPageSize = 30;
  unsharedItems: WallUnsharedViewItem[] = [];
  unsharedLayoutRows: GalleryLayoutRow<
    (WallUnsharedViewItem & { _id: string }) | WallSkeletonWork
  >[] = [];
  unsharedLayoutTotalHeight = 0;
  unsharedLayoutGap: number = GALLERY_ROWS_TUNABLES.gapDesktop;
  unsharedSkeletonSlots: WallSkeletonWork[] = [];
  isLoadingUnshared = false;
  isLoadingMoreUnshared = false;
  unsharedHasMore = true;
  unsharedLoadError = false;
  private unsharedSeenIds = new Set<string>();
  private unsharedFeedSub: Subscription;
  private unsharedShareSubs: { [id: string]: Subscription } = {};
  private unsharedObserver: IntersectionObserver | null = null;
  private publishedObserver: IntersectionObserver | null = null;
  private unsharedCatchUpRaf: number | null = null;
  private publishedCatchUpRaf: number | null = null;
  private balanceRelayoutTimer: ReturnType<typeof setTimeout> | null = null;
  private resizeLayoutTimer: ReturnType<typeof setTimeout> | null = null;
  private lastPublishedLayoutWidth = 0;
  private lastUnsharedLayoutWidth = 0;
  private layoutResizeObserver: ResizeObserver | null = null;
  /** Cap how much each works-tab may grow beyond its text size. */
  private readonly worksTabMaxGrowPx = 50;
  private worksTabSizeRaf: number | null = null;

  private sessionMeta: WallSessionMeta | null = null;
  /** Avoid reloading the same draft session repeatedly */
  private sessionSignature = '';
  private directionSub: Subscription;
  private darkSub: Subscription;
  private nameSub: Subscription;
  private userSub: Subscription;
  private userFetchSub: Subscription;
  private publishSub: Subscription;
  private wallPollSub: Subscription;
  private wallFetchSub: Subscription;
  private sessionFilesSub: Subscription;
  private draftCollapseTimer: ReturnType<typeof setTimeout> | null = null;
  private saveItemSubs: { [itemId: string]: Subscription } = {};
  private rotateItemSubs: { [itemId: string]: Subscription } = {};
  private rotateItemGen: { [itemId: string]: number } = {};
  private hideItemSubs: { [itemId: string]: Subscription } = {};
  private hideItemGen: { [itemId: string]: number } = {};
  private deleteItemSubs: { [itemId: string]: Subscription } = {};
  private saveProfileSub: Subscription;
  private saveStatusSub: Subscription;
  private saveAcademySub: Subscription;
  private clustersSub: Subscription;
  private commentsCloseTimers: { [id: string]: ReturnType<typeof setTimeout> } = {};
  private readonly commentsAnimMs = 480;
  private bodyOverflowPrev: string | null = null;
  private docOverflowPrev: string | null = null;
  private wallScrollerOverflowPrev: string | null = null;
  private wallScrollerEl: HTMLElement | null = null;
  private focusSlotHeights: { [id: string]: number } = {};
  private focusFlipUntil = 0;
  private focusAdaptQueued = false;
  private focusCappedIds = new Set<string>();
  private focusAdaptRaf: number | null = null;
  private focusOpenRaf: number | null = null;
  private focusSettleTimer: ReturnType<typeof setTimeout> | null = null;
  private publishedHydrateGen = 0;
  private unsharedHydrateGen = 0;
  private draftHydrateGen = 0;
  private focusFlipOrigin: {
    left: number;
    top: number;
    width: number;
    height: number;
  } | null = null;
  private readonly onFocusOutsidePointerDown = (event: PointerEvent): void => {
    this.handleFocusOutsidePointerDown(event);
  };
  private readonly onFocusViewportChange = (): void => {
    this.scheduleFocusAdapt();
  };

  constructor(
    private host: ElementRef<HTMLElement>,
    private ngZone: NgZone,
    private cdr: ChangeDetectorRef,
    private authService: AuthService,
    private directionService: DirectionService,
    private wallSessionService: WallSessionService,
    private wallApiService: WallApiService,
    private rowsLayout: GalleryRowsLayoutService,
    private usersService: UsersService,
    private clustersService: ClustersService,
    private router: Router,
    private dialogService: DialogService,
    private snackBar: MatSnackBar,
    private translate: TranslateService
  ) {}

  ngOnInit(): void {
    // Apply before first paint so boot spinner can fill the area under the navbar.
    this.lockStableScrollbarGutter();
    if (!this.authService.getIsAuth() || !this.authService.getUserId()) {
      this.unlockStableScrollbarGutter();
      this.router.navigate(['/auth/login']);
      return;
    }
    this.worksTab =
      localStorage.getItem('haveWall') === 'true' ? 'published' : 'unshared';

    this.directionSub = this.directionService.direction$.subscribe((direction) => {
      this.isRTL = direction === 'rtl';
    });
    this.darkSub = this.directionService.isDarkMode$.subscribe((isDarkMode) => {
      this.isDarkMode = isDarkMode;
    });
    this.nameSub = this.authService.userName$.subscribe((name) => {
      // Auth "userName" is often the username/email — prefer displayName from profile.
      if (!this.profileNameLoaded && !this.editingName && !this.isNameDirty()) {
        this.userName = this.pickWallUserName({ username: name });
        this.baselineName = this.userName;
        this.draftName = this.userName;
      }
    });
    this.userSub = this.authService.userUpdated$.subscribe((user) => {
      this.applyUserProfile(user);
    });
    this.loadUserProfile();
    this.loadSessionFiles();
    this.sessionFilesSub = this.wallSessionService.sessionFiles$.subscribe(
      (files) => {
        if (this.isPublishing || this.isDraftCollapsing) {
          return;
        }
        const meta = this.wallSessionService.getWallSessionMeta();
        const signature = this.buildSessionSignature(files, meta);
        if (signature === this.sessionSignature) {
          return;
        }
        this.loadSessionFiles();
      }
    );
    this.loadAcademyOptions();
    this.fetchPublishedWorks(true);
    this.loadUnsharedInitial();
    this.startWallPolling();
  }

  goToExhibition(): void {
    this.router.navigate(['/exhibition']);
  }

  goToNewOrder(): void {
    const printingService = localStorage.getItem('printingService');
    const branch = localStorage.getItem('branch');
    if (
      printingService &&
      printingService !== 'null' &&
      printingService !== '' &&
      branch &&
      branch !== 'null' &&
      branch !== ''
    ) {
      this.dialogService.onOpenRightPlaceDialog();
    } else if (branch && branch !== 'null' && branch !== '') {
      this.router.navigate(['/branch']);
    } else {
      const current = this.router.url.split('?')[0];
      if (current !== '/' && current !== '') {
        this.router.navigate(['/']);
      }
    }
  }

  ngAfterViewInit(): void {
    this.bindLayoutObserver();
    setTimeout(() => this.setupUnsharedObserver(), 0);
    this.bindFocusViewport();
    document.addEventListener('pointerdown', this.onFocusOutsidePointerDown, true);
    this.scheduleWorksTabSizeSync();
    this.bindScrollListener();
  }

  private bindScrollListener(): void {
    this.unbindScrollListener();
    this.scrollListener = bindWallScrollCollapse(
      this.host.nativeElement,
      (scrolled) => {
        this.ngZone.run(() => {
          this.isScrolledDown = scrolled;
        });
      },
    );
  }

  private unbindScrollListener(): void {
    this.scrollListener?.();
    this.scrollListener = null;
  }

  shareNavTooltipEnabled(): boolean {
    return this.isScrolledDown && isWallDesktopViewport();
  }

  @HostListener('window:resize')
  @HostListener('window:orientationchange')
  onWindowResize(): void {
    this.scheduleRowsRelayoutFromResize();
    this.scheduleWorksTabSizeSync();
    if (this.commentsTargetId) {
      this.scheduleFocusAdapt();
    }
  }

  @HostListener('wall-focus-relayout')
  onWallFocusRelayout(): void {
    this.scheduleFocusAdapt();
  }

  private bindFocusViewport(): void {
    this.unbindFocusViewport();
    if (typeof window === 'undefined') {
      return;
    }
    window.visualViewport?.addEventListener('resize', this.onFocusViewportChange);
    window.visualViewport?.addEventListener('scroll', this.onFocusViewportChange);
  }

  private unbindFocusViewport(): void {
    if (typeof window === 'undefined') {
      return;
    }
    window.visualViewport?.removeEventListener('resize', this.onFocusViewportChange);
    window.visualViewport?.removeEventListener('scroll', this.onFocusViewportChange);
  }

  ngOnDestroy(): void {
    // Leaving the wall without publishing discards the draft share session.
    if (!this.isPublishing) {
      this.wallSessionService.clearWallSessionFiles();
    }
    this.unbindScrollListener();
    this.directionSub?.unsubscribe();
    this.darkSub?.unsubscribe();
    this.nameSub?.unsubscribe();
    this.userSub?.unsubscribe();
    this.userFetchSub?.unsubscribe();
    this.publishSub?.unsubscribe();
    this.wallPollSub?.unsubscribe();
    this.wallFetchSub?.unsubscribe();
    this.sessionFilesSub?.unsubscribe();
    this.unsharedFeedSub?.unsubscribe();
    Object.values(this.unsharedShareSubs).forEach((sub) => sub?.unsubscribe());
    this.unsharedObserver?.disconnect();
    this.unsharedObserver = null;
    this.publishedObserver?.disconnect();
    this.publishedObserver = null;
    this.unbindLayoutObserver();
    if (this.worksTabSizeRaf != null) {
      cancelAnimationFrame(this.worksTabSizeRaf);
      this.worksTabSizeRaf = null;
    }
    if (this.resizeLayoutTimer) {
      clearTimeout(this.resizeLayoutTimer);
      this.resizeLayoutTimer = null;
    }
    if (this.unsharedCatchUpRaf != null) {
      cancelAnimationFrame(this.unsharedCatchUpRaf);
      this.unsharedCatchUpRaf = null;
    }
    if (this.publishedCatchUpRaf != null) {
      cancelAnimationFrame(this.publishedCatchUpRaf);
      this.publishedCatchUpRaf = null;
    }
    if (this.focusAdaptRaf != null) {
      cancelAnimationFrame(this.focusAdaptRaf);
      this.focusAdaptRaf = null;
    }
    this.cancelPendingFocusOpen();
    if (this.balanceRelayoutTimer) {
      clearTimeout(this.balanceRelayoutTimer);
      this.balanceRelayoutTimer = null;
    }
    this.unbindFocusViewport();
    this.unlockStableScrollbarGutter();
    document.removeEventListener('pointerdown', this.onFocusOutsidePointerDown, true);
    this.unlockBodyScroll();
    Object.values(this.commentsCloseTimers).forEach((t) => clearTimeout(t));
    Object.values(this.saveItemSubs).forEach((sub) => sub?.unsubscribe());
    Object.values(this.rotateItemSubs).forEach((sub) => sub?.unsubscribe());
    Object.values(this.hideItemSubs).forEach((sub) => sub?.unsubscribe());
    Object.values(this.deleteItemSubs).forEach((sub) => sub?.unsubscribe());
    this.saveProfileSub?.unsubscribe();
    this.saveStatusSub?.unsubscribe();
    this.saveAcademySub?.unsubscribe();
    this.clustersSub?.unsubscribe();
    if (this.draftCollapseTimer) {
      clearTimeout(this.draftCollapseTimer);
      this.draftCollapseTimer = null;
    }
    if (this.justAddedClearTimer) {
      clearTimeout(this.justAddedClearTimer);
      this.justAddedClearTimer = null;
    }
    if (this.revealScrollRaf != null) {
      cancelAnimationFrame(this.revealScrollRaf);
      this.revealScrollRaf = null;
    }
    if (this.contactTipHideTimer) {
      clearTimeout(this.contactTipHideTimer);
      this.contactTipHideTimer = null;
    }
  }

  get hasSelectedPages(): boolean {
    return this.wallFiles.some((file) => file.included);
  }

  get selectedWorksCount(): number {
    return this.wallFiles.filter((file) => file.included).length;
  }

  get publishButtonKey(): string {
    if (this.isPublishing) {
      return 'wall.personal.publish-loading';
    }
    return this.selectedWorksCount === 1
      ? 'wall.personal.publish-one'
      : 'wall.personal.publish';
  }

  get pagedPublishedItems(): WallPublishedViewItem[] {
    return this.publishedItems.slice(0, this.publishedVisibleCount);
  }

  /** Count of shared works with a loaded image (excludes loading placeholders). */
  get publishedReadyCount(): number {
    return this.publishedItems.reduce(
      (n, item) => n + (item.imgLoaded ? 1 : 0),
      0
    );
  }

  get publishedHasMore(): boolean {
    return this.publishedVisibleCount < this.publishedItems.length;
  }

  loadPublishedMore(): void {
    if (!this.publishedHasMore) {
      return;
    }
    this.publishedVisibleCount = Math.min(
      this.publishedVisibleCount + this.publishedPageSize,
      this.publishedItems.length
    );
    this.applyPublishedRowsLayout(true);
    setTimeout(() => {
      this.setupPublishedObserver();
      this.queuePublishedCatchUp();
    }, 0);
  }

  setWorksTab(tab: 'published' | 'unshared' | 'profile'): void {
    if (this.worksTab === tab) {
      return;
    }
    if (this.commentsTargetId) {
      this.closeActivePublishedFocus();
    }
    this.worksTab = tab;
    this.scrollWorksTabToTop();
    this.cdr.detectChanges();
    if (tab === 'profile') {
      return;
    }
    if (tab === 'unshared') {
      setTimeout(() => {
        this.applyUnsharedRowsLayout(true);
        this.setupUnsharedObserver();
        this.queueUnsharedCatchUp();
      }, 0);
      return;
    }
    setTimeout(() => {
      this.applyPublishedRowsLayout(true);
      this.setupPublishedObserver();
      this.queuePublishedCatchUp();
    }, 0);
  }

  /** Instant scroll to top when switching works tabs. */
  private scrollWorksTabToTop(): void {
    const scroller = wallScrollParent(this.host.nativeElement);
    if (
      scroller === document.documentElement ||
      scroller === document.body
    ) {
      window.scrollTo(0, 0);
      return;
    }
    scroller.scrollTop = 0;
  }

  get unsharedReadyCount(): number {
    return this.unsharedTabCount;
  }

  /** Badge only — never driven by transient image hide/filter races. */
  private setPublishedTabCount(next: number): void {
    if (this.publishedTabCount !== next) {
      this.publishedTabCount = next;
    }
  }

  private setUnsharedTabCount(next: number): void {
    if (this.unsharedTabCount !== next) {
      this.unsharedTabCount = next;
    }
  }

  private clampPublishedVisible(): void {
    const len = this.publishedItems.length;
    if (!len) {
      this.publishedVisibleCount = 0;
      return;
    }
    if (this.publishedVisibleCount <= 0) {
      this.publishedVisibleCount = Math.min(this.publishedPageSize, len);
      return;
    }
    if (this.publishedVisibleCount > len) {
      this.publishedVisibleCount = len;
    }
  }

  hasSavedAbout(): boolean {
    return String(this.baselineAbout || '').trim().length > 0;
  }

  isAboutDirty(): boolean {
    return String(this.draftAbout || '') !== String(this.baselineAbout || '');
  }

  isNameDirty(): boolean {
    return (
      String(this.draftName || '').trim() !==
      String(this.baselineName || '').trim()
    );
  }

  isNameValid(): boolean {
    const n = String(this.draftName || '').trim();
    return n.length >= 3 && n.length <= 40;
  }

  isProfileDirty(): boolean {
    return this.isAboutDirty() || this.isNameDirty();
  }

  startEditName(event?: Event): void {
    event?.stopPropagation();
    if (this.savingProfile) {
      return;
    }
    this.draftName = this.baselineName || this.userName || '';
    this.editingName = true;
  }

  cancelEditName(event?: Event): void {
    event?.preventDefault();
    event?.stopPropagation();
    if (this.savingProfile) {
      return;
    }
    this.draftName = this.baselineName || this.userName || '';
    this.editingName = false;
  }

  /** Empty about: always editable input. Saved about: edit mode toggled. */
  isAboutInEditMode(): boolean {
    return !this.hasSavedAbout() || this.editingAbout;
  }

  startEditAbout(event?: Event): void {
    event?.stopPropagation();
    this.editingAbout = true;
  }

  cancelEditAbout(event?: Event): void {
    event?.preventDefault();
    event?.stopPropagation();
    if (this.savingProfile) {
      return;
    }
    this.draftAbout = this.baselineAbout || '';
    this.editingAbout = false;
  }

  clearAbout(event?: Event): void {
    event?.stopPropagation();
    this.draftAbout = '';
    if (this.hasSavedAbout()) {
      this.editingAbout = true;
    }
  }

  saveProfile(): void {
    const nameDirty = this.isNameDirty();
    const aboutDirty = this.isAboutDirty();
    if (this.savingProfile || (!nameDirty && !aboutDirty)) {
      return;
    }
    if (nameDirty && !this.isNameValid()) {
      return;
    }
    this.savingProfile = true;
    const about = String(this.draftAbout || '').trim();
    const displayName = String(this.draftName || '').trim();
    const about$ = aboutDirty
      ? this.wallApiService.updateWallProfile({ about })
      : of(null);
    const name$ = nameDirty
      ? this.usersService.updateUserProfile(
          this.authService.getUserId(),
          displayName,
          this.userEmail || '',
        )
      : of(null);
    this.saveProfileSub?.unsubscribe();
    this.saveProfileSub = forkJoin({ about: about$, name: name$ }).subscribe({
        next: (res) => {
          const wall = res?.about?.wall;
          if (wall) {
            const saved =
              wall.about != null ? String(wall.about) : about;
            this.baselineAbout = saved;
            this.draftAbout = saved;
            this.editingAbout = false;
            this.editingAcademy = false;
            this.applyStatusFromWall(wall);
            this.authService.setHaveWall(true);
          }
          const user = res?.name?.user;
          if (user) {
            this.editingName = false;
            this.editingAcademy = false;
            this.authService.updateUser(user);
            this.applyUserProfile(user, true);
          }
          this.editingName = false;
          this.editingAbout = false;
          this.editingAcademy = false;
          this.savingProfile = false;
          this.showSnack('wall.personal.save-profile-success');
        },
        error: () => {
          this.savingProfile = false;
          this.showSnack('wall.personal.save-profile-error');
        },
      });
  }

  onLookingForWorkChange(checked: boolean): void {
    this.lookingForWork = checked;
    if (checked) {
      this.allowContact = true;
    }
    this.saveStatusToggles();
  }

  onOfferingHelpChange(checked: boolean): void {
    this.offeringHelp = checked;
    if (checked) {
      this.allowContact = true;
    }
    this.saveStatusToggles();
  }

  onAllowContactChange(event: MatSlideToggleChange, tip?: MatTooltip): void {
    const checked = event.checked === true;
    if (!checked && (this.lookingForWork || this.offeringHelp)) {
      event.source.checked = true;
      this.allowContact = true;
      this.showContactLockedTip = true;
      if (this.contactTipHideTimer) {
        clearTimeout(this.contactTipHideTimer);
        this.contactTipHideTimer = null;
      }
      setTimeout(() => {
        tip?.show();
        this.contactTipHideTimer = setTimeout(() => {
          tip?.hide();
          this.showContactLockedTip = false;
          this.contactTipHideTimer = null;
        }, 5500);
      }, 0);
      return;
    }
    this.allowContact = checked;
    this.saveStatusToggles();
  }

  private saveStatusToggles(): void {
    this.savingStatus = true;
    const payload = {
      lookingForWork: this.lookingForWork === true,
      offeringHelp: this.offeringHelp === true,
      allowContact: this.allowContact !== false,
    };
    this.saveStatusSub?.unsubscribe();
    this.saveStatusSub = this.wallApiService
      .updateWallProfile(payload)
      .subscribe({
        next: (res) => {
          this.applyStatusFromWall(res?.wall);
          this.savingStatus = false;
          this.authService.setHaveWall(true);
        },
        error: () => {
          this.savingStatus = false;
          this.showSnack('wall.personal.save-profile-error');
        },
      });
  }

  private applyStatusFromWall(wall?: {
    lookingForWork?: boolean;
    offeringHelp?: boolean;
    allowContact?: boolean;
    academySerialName?: string;
  } | null): void {
    if (!wall) {
      return;
    }
    if (wall.lookingForWork != null) {
      this.lookingForWork = wall.lookingForWork === true;
    }
    if (wall.offeringHelp != null) {
      this.offeringHelp = wall.offeringHelp === true;
    }
    this.allowContact = wall.allowContact !== false;
    if (wall.academySerialName != null) {
      const sn = String(wall.academySerialName || '').trim();
      this.academySerialName = this.isPrintingServiceSerial(sn) ? '' : sn;
      this.academyIsSuggested = false;
      this.applySuggestedAcademy();
    }
  }

  onAcademyChange(serialName: string): void {
    const next = String(serialName || '').trim();
    if (
      next === String(this.academySerialName || '').trim() &&
      !this.academyIsSuggested
    ) {
      this.editingAcademy = false;
      return;
    }
    this.academySerialName = next;
    this.academyIsSuggested = false;
    this.savingAcademy = true;
    this.saveAcademySub?.unsubscribe();
    this.saveAcademySub = this.wallApiService
      .updateWallProfile({ academySerialName: next })
      .subscribe({
        next: (res) => {
          this.applyStatusFromWall(res?.wall);
          this.savingAcademy = false;
          this.editingAcademy = false;
          this.authService.setHaveWall(true);
        },
        error: () => {
          this.savingAcademy = false;
          this.showSnack('wall.personal.save-profile-error');
        },
      });
  }

  /**
   * Opening wall from order-summary / my-orders: if the user has no campus yet,
   * take the print's branch/printer serial as academy immediately (creates wall if needed).
   */
  private ensureAcademyFromOrderSession(): void {
    if (this.academyFromSessionEnsured || this.savingAcademy) {
      return;
    }
    if (String(this.academySerialName || '').trim() && !this.academyIsSuggested) {
      this.academyFromSessionEnsured = true;
      return;
    }
    const sessionSerial = String(this.sessionMeta?.serialName || '').trim();
    if (!sessionSerial || this.isPrintingServiceSerial(sessionSerial)) {
      return;
    }
    if (!this.sessionMeta?.orderId && !this.wallFiles.length) {
      return;
    }

    this.academyFromSessionEnsured = true;
    this.academySerialName = sessionSerial;
    this.academyIsSuggested = false;
    this.ensureSelectedAcademyInOptions();
    this.savingAcademy = true;
    this.saveAcademySub?.unsubscribe();
    this.saveAcademySub = this.wallApiService
      .updateWallProfile({ academySerialName: sessionSerial })
      .subscribe({
        next: (res) => {
          this.applyStatusFromWall(res?.wall);
          this.ensureSelectedAcademyInOptions();
          this.savingAcademy = false;
          this.authService.setHaveWall(true);
        },
        error: () => {
          this.savingAcademy = false;
          /* keep optimistic academySerialName for UI */
        },
      });
  }

  startEditAcademy(): void {
    if (this.savingAcademy) {
      return;
    }
    this.editingAcademy = true;
    setTimeout(() => this.academySelect?.open(), 0);
  }

  cancelEditAcademy(event?: Event): void {
    event?.preventDefault();
    event?.stopPropagation();
    if (this.savingAcademy) {
      return;
    }
    this.editingAcademy = false;
  }

  onAcademyPanelOpenedChange(opened: boolean): void {
    if (!opened && !this.savingAcademy) {
      this.editingAcademy = false;
    }
  }

  academyLogoUrl(serialName: string): string {
    const sn = String(serialName || '').trim();
    if (!sn) {
      return '';
    }
    return `assets/images/branches/${sn}.png`;
  }

  onAcademyLogoError(event: Event): void {
    const img = event?.target as HTMLImageElement | null;
    if (img) {
      img.style.display = 'none';
    }
  }

  academyLabelKey(serialName: string): string {
    const sn = String(serialName || '').trim();
    if (!sn || sn === 'express' || sn === 'plotter') {
      return '';
    }
    return `choose-branch.naming.${sn}`;
  }

  /** Translated campus name; never show raw i18n keys like choose-branch.naming.express. */
  academyDisplayName(serialName: string): string {
    const key = this.academyLabelKey(serialName);
    if (!key) {
      return '';
    }
    const translated = String(this.translate.instant(key) || '').trim();
    if (translated && translated !== key) {
      return translated;
    }
    return String(serialName || '').trim();
  }

  private clustersCache: any[] = [];

  private loadAcademyOptions(): void {
    if (this.academyOptionsLoaded) {
      this.normalizeAcademyToClusterSerial(this.clustersCache);
      this.ensureSelectedAcademyInOptions();
      return;
    }
    this.academyOptionsLoaded = true;
    this.clustersSub?.unsubscribe();
    this.clustersSub = this.clustersService.getClusters().subscribe({
      next: (clusters) => {
        this.clustersCache = clusters || [];
        // Academic = plotter branch without isNotAcademy
        this.academyOptions = this.clustersCache
          .filter((cluster: any) => {
            const plotters = Array.isArray(cluster?.plotter)
              ? cluster.plotter
              : cluster?.plotter
                ? [cluster.plotter]
                : [];
            return plotters.some(
              (branch: any) => branch && branch.isNotAcademy !== true
            );
          })
          .map((cluster: any) => {
            const serialName = String(
              cluster?.serial_name || cluster?.serialName || ''
            ).trim();
            return {
              serialName,
              labelKey: `choose-branch.naming.${serialName}`,
            };
          })
          .filter((opt) => !!opt.serialName)
          .sort((a, b) => a.serialName.localeCompare(b.serialName));

        this.normalizeAcademyToClusterSerial(this.clustersCache);
        this.ensureSelectedAcademyInOptions();
        this.applySuggestedAcademy();
      },
      error: () => {
        this.academyOptionsLoaded = false;
      },
    });
  }

  private isPrintingServiceSerial(sn: string): boolean {
    const s = String(sn || '').trim().toLowerCase();
    return s === 'express' || s === 'plotter';
  }

  /** If wall stored a printer serial (or printingService), map it to the parent cluster serial_name. */
  private normalizeAcademyToClusterSerial(clusters: any[]): void {
    if (this.academyIsSuggested) {
      return;
    }
    let current = String(this.academySerialName || '').trim();
    if (this.isPrintingServiceSerial(current)) {
      // Mistakenly stored printingService — recover from published/session printer serial.
      const hint = String(
        this.publishedItems.find((i) => String(i.serialName || '').trim())
          ?.serialName ||
          this.sessionMeta?.serialName ||
          ''
      ).trim();
      if (!hint || this.isPrintingServiceSerial(hint)) {
        this.academySerialName = '';
        return;
      }
      current = hint;
      this.academySerialName = hint;
    }
    if (!current) {
      return;
    }

    let resolved = current;
    for (const cluster of clusters || []) {
      const clusterSerial = String(
        cluster?.serial_name || cluster?.serialName || ''
      ).trim();
      if (!clusterSerial) {
        continue;
      }
      if (clusterSerial === current) {
        resolved = clusterSerial;
        break;
      }
      const plotters = Array.isArray(cluster?.plotter)
        ? cluster.plotter
        : cluster?.plotter
          ? [cluster.plotter]
          : [];
      const expresses = Array.isArray(cluster?.express)
        ? cluster.express
        : cluster?.express
          ? [cluster.express]
          : [];
      const match = [...plotters, ...expresses].some(
        (b: any) =>
          String(b?.serial_name || b?.serialName || '').trim() === current
      );
      if (match) {
        resolved = clusterSerial;
        break;
      }
    }

    if (resolved !== String(this.academySerialName || '').trim()) {
      this.academySerialName = resolved;
      // Persist healed cluster serial so the raw key does not come back.
      if (!this.savingAcademy && resolved && !this.isPrintingServiceSerial(resolved)) {
        this.savingAcademy = true;
        this.saveAcademySub?.unsubscribe();
        this.saveAcademySub = this.wallApiService
          .updateWallProfile({ academySerialName: resolved })
          .subscribe({
            next: (res) => {
              this.applyStatusFromWall(res?.wall);
              this.savingAcademy = false;
            },
            error: () => {
              this.savingAcademy = false;
            },
          });
      }
    }
  }

  private ensureSelectedAcademyInOptions(): void {
    const current = String(this.academySerialName || '').trim();
    if (!current || this.isPrintingServiceSerial(current)) {
      return;
    }
    if (this.academyOptions.some((o) => o.serialName === current)) {
      return;
    }
    this.academyOptions = [
      {
        serialName: current,
        labelKey: this.academyLabelKey(current),
      },
      ...this.academyOptions,
    ];
  }

  /**
   * Wall without academy and without shared works: preselect the most recent
   * academic branch from the user's visited places, else the first academy option.
   */
  private applySuggestedAcademy(): void {
    if (
      !this.wallLoaded ||
      this.wallHasItems ||
      this.savingAcademy ||
      !this.academyOptionsLoaded ||
      !this.academyOptions.length ||
      this.recentBranches === null
    ) {
      return;
    }
    if (String(this.academySerialName || '').trim() && !this.academyIsSuggested) {
      return;
    }
    const suggested = this.pickSuggestedAcademy();
    if (!suggested) {
      return;
    }
    this.academySerialName = suggested;
    this.academyIsSuggested = true;
    this.cdr.markForCheck();
  }

  private pickSuggestedAcademy(): string {
    const optionSerials = new Set(this.academyOptions.map((o) => o.serialName));
    const branches = this.recentBranches || [];
    const services = this.recentPrintingServices || [];
    for (let i = 0; i < branches.length; i++) {
      if (String(services[i] || '').trim().toLowerCase() === 'ph') {
        continue;
      }
      const clusterSerial = this.academicClusterForVisitedBranch(branches[i]);
      if (clusterSerial && optionSerials.has(clusterSerial)) {
        return clusterSerial;
      }
    }
    return this.academyOptions[0]?.serialName || '';
  }

  /** Cluster serial of a visited branch/printer serial, or '' if unknown or not academic. */
  private academicClusterForVisitedBranch(branchSerial: string): string {
    const sn = String(branchSerial || '').trim();
    if (!sn || this.isPrintingServiceSerial(sn)) {
      return '';
    }
    const serialOf = (b: any) => String(b?.serial_name || b?.serialName || '').trim();
    const asArray = (v: any) => (Array.isArray(v) ? v : v ? [v] : []);
    for (const cluster of this.clustersCache || []) {
      const clusterSerial = serialOf(cluster);
      if (!clusterSerial) {
        continue;
      }
      if (clusterSerial === sn) {
        return clusterSerial;
      }
      const plotter = asArray(cluster?.plotter).find((b: any) => serialOf(b) === sn);
      if (plotter) {
        return plotter.isNotAcademy === true ? '' : clusterSerial;
      }
      if (asArray(cluster?.express).some((b: any) => serialOf(b) === sn)) {
        return clusterSerial;
      }
    }
    return '';
  }

  onFileToggle(file: WallFileItem, included: boolean): void {
    file.included = included;
    this.persistDraftSessionState();
  }

  onDraftImageClick(file: WallFileItem, event?: Event): void {
    event?.stopPropagation();
    file.included = !file.included;
    this.persistDraftSessionState();
  }

  rotateDraftClockwise(file: WallFileItem, event?: Event): void {
    event?.preventDefault();
    event?.stopPropagation();
    if (!file) {
      return;
    }
    file.rotation = this.nextRotation(file.rotation);
    this.wallSessionService.setDraftPageRotation(file.fileId, file.page, file.rotation);
  }

  toggleDraftHiddenFromAudience(file: WallFileItem, event?: Event): void {
    event?.preventDefault();
    event?.stopPropagation();
    if (!file) {
      return;
    }
    file.hiddenFromAudience = !file.hiddenFromAudience;
    this.persistDraftSessionState();
  }

  /** Clear sticky mobile focus/hover highlight on work action buttons. */
  private blurActiveWorkAction(): void {
    const el = document.activeElement;
    if (el instanceof HTMLElement && el.closest('.personal-wall-work-actions')) {
      el.blur();
    }
  }

  private blurWorkActionTarget(event?: Event): void {
    const t = event?.currentTarget;
    if (t instanceof HTMLElement) {
      t.blur();
    } else {
      this.blurActiveWorkAction();
    }
  }

  /**
   * Published item: apply rotation immediately and persist (no "שמור שינויים").
   */
  rotatePublishedClockwise(item: WallPublishedViewItem, event?: Event): void {
    event?.preventDefault();
    event?.stopPropagation();
    this.blurWorkActionTarget(event);
    if (!item?._id || item.deleting) {
      return;
    }
    const next = this.nextRotation(item.draftRotation);
    item.draftRotation = next;
    item.rotation = next;
    this.applyPublishedRowsLayout(true);
    if (this.isPublishedFocus(item) && item.commentsOpen) {
      this.relayoutFocusAfterRotation(item._id);
    }

    // Pending share from unshared: rotation is local until "שתף עבודה".
    if (item.pendingShareFromUnshared) {
      item.baselineRotation = next;
      return;
    }

    const gen = (this.rotateItemGen[item._id] || 0) + 1;
    this.rotateItemGen[item._id] = gen;
    this.rotateItemSubs[item._id]?.unsubscribe();
    this.rotateItemSubs[item._id] = this.wallApiService
      .updateItemRotation(item._id, next)
      .subscribe({
        next: (res) => {
          if (this.rotateItemGen[item._id] !== gen) {
            return;
          }
          const saved = this.normalizeRotation(
            res?.item?.rotation != null ? res.item.rotation : next
          );
          item.rotation = saved;
          item.draftRotation = saved;
          item.baselineRotation = saved;
          delete this.rotateItemSubs[item._id];
        },
        error: () => {
          if (this.rotateItemGen[item._id] !== gen) {
            return;
          }
          const confirmed = this.normalizeRotation(item.baselineRotation);
          item.draftRotation = confirmed;
          item.rotation = confirmed;
          delete this.rotateItemSubs[item._id];
          this.applyPublishedRowsLayout(true);
          if (this.isPublishedFocus(item) && item.commentsOpen) {
            this.relayoutFocusAfterRotation(item._id);
          }
          this.showSnack('wall.personal.save-error');
        },
      });
  }

  /** After image rotate: wait for aspect-ratio paint, then resize the focus dialog. */
  private relayoutFocusAfterRotation(cardId: string): void {
    if (this.commentsTargetId !== cardId) {
      return;
    }
    this.ngZone.runOutsideAngular(() => {
      requestAnimationFrame(() => {
        this.ngZone.run(() => {
          if (this.commentsTargetId !== cardId) {
            return;
          }
          this.cdr.detectChanges();
          requestAnimationFrame(() => {
            if (this.commentsTargetId !== cardId) {
              return;
            }
            if (this.publishedFocusMode === 'edit') {
              this.relayoutFocusAfterEditMode(cardId);
              return;
            }
            // Social: clear fill/pin so a shorter landscape (or taller portrait) can settle.
            this.focusCappedIds.delete(cardId);
            const panel = this.getFocusPanelEl(cardId);
            panel?.classList.remove('personal-wall-card-focus-fill');
            panel?.style.removeProperty('height');
            this.syncFocusPanelPinnedHeight(cardId, true);
            this.syncFocusBodyScroll(cardId);
          });
        });
      });
    });
  }

  onWallImageLoad(
    event: Event,
    item: WallFileItem | WallPublishedViewItem
  ): void {
    const img = event.target as HTMLImageElement;
    if (isWallPlaceholderThumb(img?.currentSrc || img?.src || item?.thumbUrl)) {
      this.hideWorkWithoutImage(item);
      return;
    }
    if (img?.naturalWidth && img.naturalHeight) {
      const prevW = item.natW;
      const prevH = item.natH;
      item.natW = img.naturalWidth;
      item.natH = img.naturalHeight;
      item.imgLoaded = true;
      const rotation =
        (item as WallPublishedViewItem).draftRotation != null
          ? (item as WallPublishedViewItem).draftRotation
          : (item as WallFileItem).rotation;
      if (
        wallDisplayedAspectChanged(
          prevW,
          prevH,
          item.natW,
          item.natH,
          rotation
        )
      ) {
        this.scheduleBalancedRelayout();
      }
    } else {
      item.imgLoaded = true;
    }
    const pub = item as WallPublishedViewItem;
    if (pub._id && this.commentsTargetId === pub._id && pub.commentsOpen) {
      this.scheduleFocusAdapt();
    }
  }

  onImageError(
    _event: Event,
    item?: WallFileItem | WallPublishedViewItem
  ): void {
    if (item) {
      this.hideWorkWithoutImage(item);
    }
  }

  private hideWorkWithoutImage(
    item: WallFileItem | WallPublishedViewItem
  ): void {
    if ((item as WallPublishedViewItem)?._id && !('included' in item)) {
      const id = String((item as WallPublishedViewItem)._id);
      const before = this.publishedItems.length;
      this.publishedItems = this.publishedItems.filter((p) => p._id !== id);
      if (this.publishedItems.length !== before) {
        // Image failed — drop from collage only; keep tab badge on last server count.
        this.clampPublishedVisible();
        this.applyPublishedRowsLayout(true);
      }
      return;
    }
    const draft = item as WallFileItem;
    if (!draft?.id) {
      return;
    }
    const before = this.wallFiles.length;
    this.wallFiles = this.wallFiles.filter((f) => f.id !== draft.id);
    if (this.wallFiles.length !== before) {
      this.cdr.markForCheck();
    }
  }

  imageTransform(rotation: number | undefined): string {
    const r = this.normalizeRotation(rotation);
    return r ? `rotate(${r}deg)` : 'none';
  }

  isSideways(rotation: number | undefined): boolean {
    return this.normalizeRotation(rotation) === 90
      || this.normalizeRotation(rotation) === 270;
  }

  /** Displayed width/height units for draft thumb CSS vars (current rotation). */
  draftArW(file: WallFileItem): number {
    const { w } = wallDisplayedSize(file?.natW, file?.natH, file?.rotation);
    return w > 0 ? w : 3;
  }

  draftArH(file: WallFileItem): number {
    const { h } = wallDisplayedSize(file?.natW, file?.natH, file?.rotation);
    return h > 0 ? h : 4;
  }

  @HostListener('document:keydown.escape')
  onEscapeClosePublishedFocus(): void {
    if (!this.commentsTargetId) {
      return;
    }
    const item = this.publishedItems.find((c) => c._id === this.commentsTargetId);
    if (item) {
      this.closePublishedFocus(item);
    }
  }

  mediaAspectRatio(
    item: WallImageFitState,
    rotation: number | undefined
  ): string {
    if (!item.natW || !item.natH) {
      return '3 / 4';
    }
    return this.isSideways(rotation)
      ? `${item.natH} / ${item.natW}`
      : `${item.natW} / ${item.natH}`;
  }

  mediaAspectValue(item: WallPublishedViewItem): string {
    const { w, h } = wallDisplayedSize(item.natW, item.natH, item.draftRotation);
    if (w > 0 && h > 0) {
      return String(w / h);
    }
    if (this.commentsTargetId === item._id && this.focusFlipOrigin) {
      const { width, height } = this.focusFlipOrigin;
      if (width > 1 && height > 1) {
        return String(width / height);
      }
    }
    return '0.75';
  }

  isFocusPanoramic(item: WallPublishedViewItem): boolean {
    return isWallDesktopViewport() && isWallImagePanoramic(item.natW, item.natH, item.draftRotation);
  }

  isFocusHeightCapped(item: WallPublishedViewItem): boolean {
    return this.focusCappedIds.has(item._id);
  }

  isPublishedFocus(item: { _id?: string } | null | undefined): boolean {
    return !!item?._id && this.commentsTargetId === item._id;
  }

  isPublishedEditFocus(item: WallPublishedViewItem): boolean {
    return this.isPublishedFocus(item) && this.publishedFocusMode === 'edit';
  }

  isPublishedSocialFocus(item: WallPublishedViewItem): boolean {
    return this.isPublishedFocus(item) && this.publishedFocusMode === 'social';
  }

  publishedFocusSlotHeight(item: { _id?: string } | null | undefined): number | null {
    if (!item?._id || !this.isPublishedFocus(item)) {
      return null;
    }
    const h = this.focusSlotHeights[item._id];
    return h > 0 ? h : null;
  }

  isPublishedFocusVisible(): boolean {
    if (!this.commentsTargetId) {
      return false;
    }
    const item = this.publishedItems.find((c) => c._id === this.commentsTargetId);
    return !!item?.commentsMounted;
  }

  isFocusCloseVisible(): boolean {
    if (!this.commentsTargetId) {
      return false;
    }
    const item = this.publishedItems.find((c) => c._id === this.commentsTargetId);
    return !!item?.commentsOpen;
  }

  togglePublishedFocus(item: WallPublishedViewItem, event?: Event): void {
    event?.stopPropagation();
    if (item.pendingShareFromUnshared) {
      if (!this.isPublishedEditFocus(item)) {
        this.openPublishedFocus(item, 'edit');
        this.scheduleEditFocusRelayout(item);
      }
      return;
    }
    if (this.isPublishedFocus(item) && (item.commentsOpen || item.commentsMounted)) {
      this.closePublishedFocus(item);
      return;
    }
    this.openPublishedFocus(item, 'social');
  }

  openPublishedEditFocus(item: WallPublishedViewItem, event?: Event): void {
    event?.preventDefault();
    event?.stopPropagation();
    if (this.isPublishedEditFocus(item)) {
      this.closePublishedFocus(item);
      return;
    }
    if (this.isPublishedFocus(item) && this.publishedFocusMode === 'social') {
      // Already enlarged — swap chrome in place; do not re-run open FLIP.
      this.publishedFocusMode = 'edit';
      item.commentsLoading = false;
      this.focusCappedIds.delete(item._id);
      const panel = this.getFocusPanelEl(item._id);
      panel?.classList.remove('personal-wall-card-focus-fill');
      panel?.classList.remove('personal-wall-card-focus-body-scroll');
      panel?.style.removeProperty('height');
      this.cdr.detectChanges();
      this.scheduleEditFocusRelayout(item);
      return;
    }
    this.openPublishedFocus(item, 'edit');
    this.scheduleEditFocusRelayout(item);
  }

  closePublishedFocusViaUi(item: WallPublishedViewItem, event?: Event): void {
    event?.stopPropagation();
    this.closePublishedFocus(item);
  }

  closeActivePublishedFocus(): void {
    if (!this.commentsTargetId) {
      return;
    }
    const item = this.publishedItems.find((c) => c._id === this.commentsTargetId);
    if (item) {
      this.closePublishedFocus(item);
    }
  }

  onFocusCloseClick(event: Event): void {
    event.preventDefault();
    event.stopPropagation();
    this.closeActivePublishedFocus();
  }

  private openPublishedFocus(
    item: WallPublishedViewItem,
    mode: 'social' | 'edit' = 'social',
    originOverride?: {
      left: number;
      top: number;
      width: number;
      height: number;
    } | null
  ): void {
    this.publishedItems.forEach((other) => {
      if (other._id !== item._id && this.commentsTargetId === other._id) {
        other.commentsOpen = false;
        other.commentsMounted = false;
        this.clearFocusFlipStyles(other._id);
        this.focusCappedIds.delete(other._id);
        clearTimeout(this.commentsCloseTimers[other._id]);
        delete this.commentsCloseTimers[other._id];
      }
    });

    if (
      originOverride &&
      originOverride.width >= 2 &&
      originOverride.height >= 2
    ) {
      this.focusFlipOrigin = { ...originOverride };
      this.focusSlotHeights[item._id] = Math.max(1, originOverride.height);
    } else {
      const originEl =
        item.pendingShareFromUnshared && item.sourceUnsharedId
          ? ((this.host.nativeElement.querySelector(
              `[data-unshared-id="${item.sourceUnsharedId}"]`
            ) as HTMLElement | null) ||
            (this.host.nativeElement.querySelector(
              `[data-published-id="${item._id}"]`
            ) as HTMLElement | null))
          : (this.host.nativeElement.querySelector(
              `[data-published-id="${item._id}"]`
            ) as HTMLElement | null);
      const originRect = originEl?.getBoundingClientRect();
      this.focusFlipOrigin = originRect
        ? {
            left: originRect.left,
            top: originRect.top,
            width: Math.max(1, originRect.width),
            height: Math.max(1, originRect.height),
          }
        : null;
      if (originRect) {
        this.focusSlotHeights[item._id] = Math.max(1, originRect.height);
      }
    }

    // Don't pre-arm fill from the comment-count estimate — that enables
    // overflow-y:auto for a frame, then sync removes it (visible scrollbar flash).
    // Decide fill once after compose/chrome have laid out (see openFocusAfterChromeReady).
    this.focusCappedIds.delete(item._id);

    clearTimeout(this.commentsCloseTimers[item._id]);
    delete this.commentsCloseTimers[item._id];
    this.publishedFocusMode = mode;
    if (mode === 'social') {
      if (!item.comments.length && (Number(item.commentCount) || 0) > 0) {
        item.commentsLoading = true;
      }
    } else {
      item.commentsLoading = false;
    }
    item.commentsMounted = true;
    item.commentsOpen = true;
    this.commentsTargetId = item._id;
    this.lockBodyScroll();
    this.cdr.detectChanges();
    // Wait for compose/mat-field + comments chrome, then pin once and FLIP.
    this.openFocusAfterChromeReady(item._id, !!item.comments.length);
  }

  /** Drop in-flight open FLIP settle so a mid-open close cannot snap to CSS top:0. */
  private cancelPendingFocusOpen(): void {
    if (this.focusOpenRaf != null) {
      cancelAnimationFrame(this.focusOpenRaf);
      this.focusOpenRaf = null;
    }
    if (this.focusSettleTimer != null) {
      clearTimeout(this.focusSettleTimer);
      this.focusSettleTimer = null;
    }
  }

  private scheduleFocusOpenSettle(cardId: string): void {
    if (this.focusSettleTimer != null) {
      clearTimeout(this.focusSettleTimer);
    }
    this.focusSettleTimer = setTimeout(() => {
      this.focusSettleTimer = null;
      this.settleFocusPanelToCss(cardId);
    }, this.commentsAnimMs);
  }

  /** Pin height after first paint, then grow from slot — same motion as close, reversed. */
  private openFocusAfterChromeReady(cardId: string, scrollCommentsTop: boolean): void {
    this.cancelPendingFocusOpen();
    this.ngZone.runOutsideAngular(() => {
      this.focusOpenRaf = requestAnimationFrame(() => {
        this.focusOpenRaf = null;
        this.ngZone.run(() => {
          if (this.commentsTargetId !== cardId) {
            return;
          }
          const item = this.publishedItems.find((c) => c._id === cardId);
          if (!item?.commentsOpen) {
            return;
          }
          if (item && this.publishedFocusMode === 'social') {
            if (this.focusContentNeedsMaxHeight(item)) {
              this.focusCappedIds.add(cardId);
            } else {
              this.focusCappedIds.delete(cardId);
            }
          } else if (this.publishedFocusMode === 'edit') {
            this.focusCappedIds.delete(cardId);
            if (item) {
              this.fitPublishedEditFields(item);
            }
          }
          const focusPanel = this.getFocusPanelEl(cardId);
          if (focusPanel) {
            void focusPanel.offsetHeight;
            if (this.publishedFocusMode === 'edit') {
              this.relayoutFocusAfterEditMode(cardId);
            } else {
              this.syncFocusPanelPinnedHeight(cardId, true);
            }
          }
          this.focusFlipUntil = Date.now() + this.commentsAnimMs + 48;
          this.playFocusFlipOpen(cardId);
          this.scheduleFocusOpenSettle(cardId);
          if (scrollCommentsTop) {
            setTimeout(() => this.scrollFocusCommentsToTop(cardId), 0);
          }
        });
      });
    });
  }

  private scrollFocusCommentsToTop(cardId: string): void {
    const panel = this.getFocusPanelEl(cardId);
    if (!panel) {
      return;
    }
    const scroller = panel.querySelector(
      '.wall-card-social-scroll'
    ) as HTMLElement | null;
    if (scroller) {
      scroller.scrollTop = 0;
    }
    const list = panel.querySelector(
      `[data-comments-list="${cardId}"]`
    ) as HTMLElement | null;
    if (list) {
      list.scrollTop = 0;
    }
  }

  private closePublishedFocus(item: WallPublishedViewItem): void {
    if (!item.commentsMounted || !item.commentsOpen || this.commentsTargetId !== item._id) {
      return;
    }
    this.cancelPendingFocusOpen();
    this.focusFlipUntil = 0;
    this.pinFocusPanelFromRect(item._id);
    const focusPanel = this.getFocusPanelEl(item._id);
    const panelStart = focusPanel?.getBoundingClientRect() ?? null;
    const mediaStart = focusPanel
      ? wallFocusMediaEl(focusPanel)?.getBoundingClientRect() ?? null
      : null;
    item.commentsOpen = false;
    this.cdr.detectChanges();
    this.ngZone.runOutsideAngular(() => {
      requestAnimationFrame(() => {
        const played = this.playFocusFlipClose(item._id, mediaStart, panelStart);
        this.ngZone.run(() => {
          this.commentsCloseTimers[item._id] = setTimeout(() => {
            item.commentsMounted = false;
            this.clearFocusFlipStyles(item._id);
            if (this.commentsTargetId === item._id) {
              this.commentsTargetId = null;
              this.publishedFocusMode = null;
              this.focusFlipOrigin = null;
              delete this.focusSlotHeights[item._id];
              this.focusCappedIds.delete(item._id);
              this.unlockBodyScroll();
            }
            delete this.commentsCloseTimers[item._id];
            if (item.pendingShareFromUnshared) {
              this.cancelPendingUnsharedShare(item);
            }
          }, played ? this.commentsAnimMs : 0);
        });
      });
    });
  }

  private handleFocusOutsidePointerDown(event: PointerEvent): void {
    if (!this.commentsTargetId || !this.isPublishedFocusVisible()) {
      return;
    }
    const node = event.target instanceof Node ? event.target : null;
    if (!node) {
      return;
    }
    const el = node instanceof Element ? node : node.parentElement;
    // Close / backdrop live outside the panel; let their own handlers run.
    // Closing here on pointerdown removes the X before click and the event
    // falls through to the navbar (user menu).
    if (
      el?.closest(
        '.personal-wall-focus-close, .personal-wall-focus-backdrop, .cdk-overlay-container, .cdk-overlay-pane'
      )
    ) {
      return;
    }
    const panel = this.getFocusPanelEl(this.commentsTargetId);
    if (panel && panel.contains(node)) {
      return;
    }
    event.preventDefault();
    event.stopPropagation();
    this.closeActivePublishedFocus();
  }

  private focusDialogWidth(cardId?: string): number {
    if (typeof window === 'undefined') {
      return 560;
    }
    if (!isWallDesktopViewport()) {
      return window.innerWidth;
    }
    const wide = window.innerWidth >= 900;
    const standard = Math.min(window.innerWidth * (wide ? 0.9 : 0.94), wide ? 640 : 560);
    const id = cardId || this.commentsTargetId;
    const item = id ? this.publishedItems.find((c) => c._id === id) : undefined;
    if (item && isWallImagePanoramic(item.natW, item.natH, item.draftRotation)) {
      return Math.max(standard, window.innerWidth - 100);
    }
    return standard;
  }

  private focusDialogMaxHeight(): number {
    if (typeof window === 'undefined') {
      return 900;
    }
    if (!isWallDesktopViewport()) {
      const visible = window.visualViewport?.height || window.innerHeight;
      return Math.max(1, Math.round(visible * 0.9));
    }
    return Math.min(window.innerHeight - 24, 900);
  }

  private focusContentNeedsMaxHeight(item: WallPublishedViewItem): boolean {
    const n = Math.max(
      Number(item.commentCount) || 0,
      Array.isArray(item.comments) ? item.comments.length : 0
    );
    if (n <= 0 || typeof window === 'undefined') {
      return false;
    }
    const maxH = this.focusDialogMaxHeight();
    const imgMax = Math.min(window.innerHeight * 0.58, 520);
    const { w, h } = wallDisplayedSize(item.natW, item.natH, item.draftRotation);
    const panelW = this.focusDialogWidth(item._id);
    let imgH = imgMax;
    if (w > 0 && h > 0) {
      const ar = w / h;
      const imgW = Math.min(panelW, Math.max(1, imgMax * ar));
      imgH = Math.min(imgMax, imgW / Math.max(0.01, ar));
    }
    return imgH + 230 + n * 72 > maxH - 4;
  }

  private focusDialogLeft(finalW: number): number {
    if (typeof window === 'undefined' || !isWallDesktopViewport()) {
      return 0;
    }
    return Math.max(0, (window.innerWidth - finalW) / 2);
  }

  private focusDialogTop(finalH: number): number {
    if (typeof window === 'undefined') {
      return 12;
    }
    if (!isWallDesktopViewport()) {
      return Math.max(0, window.innerHeight - finalH);
    }
    const margin = 12;
    const maxTop = Math.max(margin, window.innerHeight - finalH - margin);
    const centered = (window.innerHeight - finalH) / 2;
    return Math.max(margin, Math.min(centered, maxTop));
  }

  private getFocusPanelEl(cardId: string): HTMLElement | null {
    return this.host.nativeElement.querySelector(
      `[data-focus-panel="${cardId}"]`
    ) as HTMLElement | null;
  }

  private getFocusSlotEl(cardId: string): HTMLElement | null {
    return this.host.nativeElement.querySelector(
      `[data-published-id="${cardId}"]`
    ) as HTMLElement | null;
  }

  private clearFocusFlipStyles(cardId: string): void {
    const panel = this.getFocusPanelEl(cardId);
    if (!panel) {
      return;
    }
    panel.style.left = '';
    panel.style.top = '';
    panel.style.right = '';
    panel.style.bottom = '';
    panel.style.width = '';
    panel.style.maxWidth = '';
    panel.style.maxHeight = '';
    panel.style.removeProperty('height');
    panel.style.transform = '';
    panel.style.transformOrigin = '';
    panel.style.transition = '';
    panel.style.borderRadius = '';
    wallFocusClearMediaFlipStyles(panel);
    panel.classList.remove('personal-wall-card-focus-returning');
    panel.classList.remove('personal-wall-card-focus-opening');
    panel.classList.remove('personal-wall-card-focus-body-scroll');
  }

  private hydrateFocusNatFromPanel(cardId: string): WallPublishedViewItem | undefined {
    const item = this.publishedItems.find((c) => c._id === cardId);
    const panel = this.getFocusPanelEl(cardId);
    if (!item || !panel || (item.natW && item.natH)) {
      return item;
    }
    const dims = wallFocusNatFromPanelImg(panel);
    if (dims) {
      item.natW = dims.natW;
      item.natH = dims.natH;
      item.imgLoaded = true;
    }
    return item;
  }

  private pinFocusPanelFromRect(cardId: string): void {
    const panel = this.getFocusPanelEl(cardId);
    if (!panel || typeof window === 'undefined') {
      return;
    }
    const r = panel.getBoundingClientRect();
    panel.style.transition = 'none';
    panel.style.transform = 'none';
    panel.classList.remove('personal-wall-card-focus-opening');
    wallFocusPinPanelToBox(panel, r);
    void panel.offsetWidth;
  }

  private focusCommentsReservePx(item: WallPublishedViewItem): number {
    return wallCommentsLoadReservePx(
      item.commentCount,
      Array.isArray(item.comments) ? item.comments.length : 0
    );
  }

  private measureFocusNaturalHeight(cardId: string): number {
    const panel = this.getFocusPanelEl(cardId);
    if (!panel) {
      return 1;
    }
    this.hydrateFocusNatFromPanel(cardId);
    const item = this.publishedItems.find((c) => c._id === cardId);
    const reserve = item ? this.focusCommentsReservePx(item) : 0;
    const contentH = this.intrinsicFocusContentHeight(panel);
    return wallFocusNaturalPanelHeight(panel, contentH, reserve);
  }

  private isFocusPanelPinned(cardId: string): boolean {
    if (Date.now() < this.focusFlipUntil) {
      return true;
    }
    const panel = this.getFocusPanelEl(cardId);
    if (!panel) {
      return false;
    }
    return (
      !!panel.style.left ||
      !!panel.style.bottom ||
      panel.style.getPropertyPriority('height') === 'important'
    );
  }

  private syncFocusPanelPinnedHeight(cardId: string, force = false): void {
    const panel = this.getFocusPanelEl(cardId);
    if (!panel || typeof window === 'undefined') {
      return;
    }
    // Freeze geometry while the open FLIP runs — mid-open re-pins cause jumps.
    if (!force && Date.now() < this.focusFlipUntil) {
      this.focusAdaptQueued = true;
      return;
    }
    this.hydrateFocusNatFromPanel(cardId);
    this.syncFocusFillFromOverflow(cardId, force);
    const finalW = this.focusDialogWidth(cardId);
    const maxH = this.focusDialogMaxHeight();
    const item = this.publishedItems.find((c) => c._id === cardId);
    const inEdit = this.publishedFocusMode === 'edit';
    const reserve =
      item && !inEdit ? this.focusCommentsReservePx(item) : 0;
    const contentH = this.intrinsicFocusContentHeight(panel);
    const naturalH = wallFocusNaturalPanelHeight(panel, contentH, reserve);
    const finalH =
      !inEdit && this.focusCappedIds.has(cardId)
        ? maxH
        : Math.min(naturalH, maxH);
    panel.style.transition = 'none';
    panel.style.transform = 'none';
    panel.style.width = `${finalW}px`;
    panel.style.maxWidth = `${finalW}px`;
    panel.style.maxHeight = `${maxH}px`;
    panel.style.setProperty('height', `${Math.round(finalH)}px`, 'important');
    if (!isWallDesktopViewport()) {
      panel.style.left = '0px';
      panel.style.right = '0px';
      panel.style.top = 'auto';
      panel.style.bottom = '0px';
    } else {
      panel.style.left = `${this.focusDialogLeft(finalW)}px`;
      panel.style.top = `${this.focusDialogTop(finalH)}px`;
      panel.style.right = '';
      panel.style.bottom = '';
    }
    void panel.offsetWidth;
  }

  /** Fit edit fields, then resize (and enable inner scroll if content exceeds max). */
  private scheduleEditFocusRelayout(item: WallPublishedViewItem): void {
    const run = () => {
      if (!this.isPublishedEditFocus(item)) {
        return;
      }
      this.fitPublishedEditFields(item);
      this.relayoutFocusAfterEditMode(item._id);
    };
    this.ngZone.runOutsideAngular(() => {
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          this.ngZone.run(run);
          setTimeout(() => this.ngZone.run(run), 80);
        });
      });
    });
  }

  /** Resize the enlarged panel to edit chrome (grow to fit; scroll only at max). */
  private relayoutFocusAfterEditMode(cardId: string, pinEnd = false): void {
    const panel = this.getFocusPanelEl(cardId);
    if (!panel || typeof window === 'undefined') {
      return;
    }
    // Mid-open remeasure clears FLIP transform/geometry and causes a visible jump.
    if (Date.now() < this.focusFlipUntil) {
      this.focusAdaptQueued = true;
      return;
    }
    const maxH = this.focusDialogMaxHeight();
    const scroll = panel.querySelector(
      '.wall-card-social-scroll'
    ) as HTMLElement | null;
    const social = panel.querySelector(
      'app-wall-card-social'
    ) as HTMLElement | null;

    // Already at max height: don't unlock (that flashes full-height). Keep the
    // save button at the end while typing.
    if (this.focusCappedIds.has(cardId) && scroll) {
      const stillOverflows = scroll.scrollHeight > maxH + 1;
      if (stillOverflows) {
        scroll.style.setProperty('overflow-y', 'auto', 'important');
        if (pinEnd) {
          this.pinEditScrollToEnd(scroll);
        }
        return;
      }
    }

    this.focusCappedIds.delete(cardId);
    panel.classList.remove('personal-wall-card-focus-fill');
    panel.classList.remove('personal-wall-card-focus-body-scroll');

    const finalW = this.focusDialogWidth(cardId);

    panel.style.transition = 'none';
    panel.style.transform = 'none';
    panel.style.width = `${finalW}px`;
    panel.style.maxWidth = `${finalW}px`;
    // Unlock CSS max-height so media + edit chrome can lay out at full size.
    panel.style.removeProperty('height');
    panel.style.setProperty('max-height', 'none', 'important');
    if (social) {
      social.style.maxHeight = 'none';
      social.style.overflow = 'visible';
    }
    if (scroll) {
      scroll.style.flex = '0 0 auto';
      scroll.style.minHeight = '0';
      scroll.style.overflow = 'visible';
      scroll.style.height = 'auto';
      scroll.style.maxHeight = 'none';
    }
    this.clearFocusScrollInline(panel, false);
    void panel.offsetWidth;

    this.hydrateFocusNatFromPanel(cardId);
    const contentH = this.measureEditFocusContentHeight(panel);
    const needsScroll = contentH > maxH + 1;
    const finalH = needsScroll
      ? maxH
      : Math.max(1, Math.min(contentH, maxH));

    if (needsScroll) {
      this.focusCappedIds.add(cardId);
      panel.classList.add('personal-wall-card-focus-fill');
    } else {
      this.focusCappedIds.delete(cardId);
      panel.classList.remove('personal-wall-card-focus-fill');
    }

    panel.style.maxHeight = `${maxH}px`;
    panel.style.setProperty('height', `${finalH}px`, 'important');
    if (social) {
      social.style.maxHeight = '';
      social.style.overflow = '';
    }
    if (scroll) {
      scroll.style.flex = '';
      scroll.style.minHeight = '';
      scroll.style.height = '';
      scroll.style.maxHeight = '';
      scroll.style.overflowX = 'hidden';
      if (needsScroll) {
        scroll.style.setProperty('overflow-y', 'auto', 'important');
      } else {
        scroll.style.removeProperty('overflow-y');
      }
    }

    if (!isWallDesktopViewport()) {
      panel.style.left = '0px';
      panel.style.right = '0px';
      panel.style.top = 'auto';
      panel.style.bottom = '0px';
    } else {
      panel.style.left = `${this.focusDialogLeft(finalW)}px`;
      panel.style.top = `${this.focusDialogTop(finalH)}px`;
      panel.style.right = '';
      panel.style.bottom = '';
    }
    void panel.offsetWidth;
    if (pinEnd && needsScroll && scroll) {
      this.pinEditScrollToEnd(scroll);
    }
    this.cdr.detectChanges();
  }

  private pinEditScrollToEnd(scroll: HTMLElement): void {
    scroll.scrollTop = Math.max(0, scroll.scrollHeight - scroll.clientHeight);
  }

  /** True content height for edit focus (media + edit chrome), not the locked box. */
  private measureEditFocusContentHeight(panel: HTMLElement): number {
    const scroll = panel.querySelector(
      '.wall-card-social-scroll'
    ) as HTMLElement | null;
    const media = panel.querySelector(
      '.personal-wall-card-media'
    ) as HTMLElement | null;
    const edit = panel.querySelector(
      '.personal-wall-edit-panel'
    ) as HTMLElement | null;
    const owner = panel.querySelector(
      '.wall-card-social-owner'
    ) as HTMLElement | null;

    const boxH = (el: HTMLElement | null): number => {
      if (!el) {
        return 0;
      }
      return Math.max(
        el.getBoundingClientRect().height,
        el.offsetHeight,
        el.scrollHeight
      );
    };

    let h = 0;
    if (media) {
      h += boxH(media);
    }
    if (edit) {
      h += boxH(edit);
    } else if (owner) {
      h += boxH(owner);
    }
    if (owner && edit) {
      const os = window.getComputedStyle(owner);
      h +=
        (parseFloat(os.paddingTop) || 0) + (parseFloat(os.paddingBottom) || 0);
    }
    if (scroll) {
      const ss = window.getComputedStyle(scroll);
      h +=
        (parseFloat(ss.paddingTop) || 0) + (parseFloat(ss.paddingBottom) || 0);
      if (edit) {
        h = Math.max(h, scroll.scrollHeight);
      }
    }
    return Math.ceil(h + 4);
  }

  private beginFocusOpenFlip(cardId: string): void {
    this.playFocusFlipOpen(cardId);
    this.scheduleFocusOpenSettle(cardId);
  }

  private afterFocusContentLayout(cardId: string): void {
    if (Date.now() < this.focusFlipUntil) {
      this.focusAdaptQueued = true;
      return;
    }
    requestAnimationFrame(() => {
      this.cdr.detectChanges();
      requestAnimationFrame(() => {
        if (this.publishedFocusMode === 'edit') {
          this.relayoutFocusAfterEditMode(cardId);
          return;
        }
        if (this.isFocusPanelPinned(cardId)) {
          this.syncFocusPanelPinnedHeight(cardId, true);
        }
        this.quietAdaptFocusHeight(cardId);
      });
    });
  }

  private settleFocusPanelToCss(cardId: string): void {
    const panel = this.getFocusPanelEl(cardId);
    const item = this.publishedItems.find((c) => c._id === cardId);
    if (!panel || this.commentsTargetId !== cardId || !item?.commentsOpen) {
      return;
    }
    // Keep the height we animated with; only drop absolute position/transform.
    const pinnedH =
      parseFloat(panel.style.height) ||
      panel.getBoundingClientRect().height ||
      0;
    this.focusFlipUntil = 0;
    this.focusAdaptQueued = false;
    panel.style.transition = 'none';
    panel.style.left = '';
    panel.style.top = '';
    panel.style.right = '';
    panel.style.bottom = '';
    panel.style.width = '';
    panel.style.maxWidth = '';
    panel.style.maxHeight = '';
    panel.style.transform = '';
    panel.style.borderRadius = '';
    wallFocusClearMediaFlipStyles(panel);
    panel.classList.remove('personal-wall-card-focus-opening');
    // Keep pinned height with !important so CSS `height: auto !important` cannot snap.
    if (pinnedH > 1) {
      panel.style.setProperty('height', `${Math.round(pinnedH)}px`, 'important');
    }
    void panel.offsetWidth;
    panel.style.transition = '';
    if (item?.commentsLoading) {
      // Comments still loading — stay pinned; don't leave opening styles stuck.
      this.syncFocusPanelPinnedHeight(cardId, true);
      return;
    }
    if (this.publishedFocusMode === 'edit') {
      if (item) {
        this.fitPublishedEditFields(item);
      }
      this.relayoutFocusAfterEditMode(cardId);
      if (item) {
        this.scheduleEditFocusRelayout(item);
      }
      return;
    }
    // Fit to content up to max; enable inner scroll only when capped at 90%.
    this.syncFocusBodyScroll(cardId);
  }

  /**
   * Fit the open panel to content up to the viewport max; scroll only when capped.
   * Avoid locking a short height with an inner scrollport (phantom mobile scroll).
   */
  private syncFocusBodyScroll(cardId: string): void {
    const panel = this.getFocusPanelEl(cardId);
    if (!panel) {
      return;
    }
    const item = this.publishedItems.find((c) => c._id === cardId);
    const reserve =
      item && this.publishedFocusMode !== 'edit'
        ? this.focusCommentsReservePx(item)
        : 0;
    const contentH = this.intrinsicFocusContentHeight(panel);
    const naturalH = wallFocusNaturalPanelHeight(panel, contentH, reserve);
    const maxH = this.focusDialogMaxHeight();
    const needsCap = naturalH > maxH + 1;
    const finalH = needsCap ? maxH : Math.max(1, Math.min(naturalH, maxH));

    if (needsCap) {
      this.focusCappedIds.add(cardId);
      panel.classList.add('personal-wall-card-focus-fill');
      panel.classList.remove('personal-wall-card-focus-body-scroll');
      panel.style.removeProperty('height');
    } else {
      this.focusCappedIds.delete(cardId);
      panel.classList.remove('personal-wall-card-focus-fill');
      panel.classList.remove('personal-wall-card-focus-body-scroll');
      panel.style.setProperty('height', `${Math.round(finalH)}px`, 'important');
    }
    this.clearFocusScrollInline(panel);
  }

  /** One-shot overflow sync after open — never changes height for small deltas. */
  private quietAdaptFocusHeight(cardId: string): void {
    if (this.commentsTargetId !== cardId || Date.now() < this.focusFlipUntil) {
      return;
    }
    const panel = this.getFocusPanelEl(cardId);
    const item = this.publishedItems.find((c) => c._id === cardId);
    if (!panel || !item?.commentsOpen) {
      return;
    }
    if (this.publishedFocusMode === 'edit') {
      this.relayoutFocusAfterEditMode(cardId);
      return;
    }
    this.syncFocusBodyScroll(cardId);
  }

  private intrinsicFocusContentHeight(panel: HTMLElement): number {
    const media = panel.querySelector(
      '.personal-wall-card-media, .eazix-exhibition-card-media'
    ) as HTMLElement | null;
    const comments = panel.querySelector(
      '.eazix-exhibition-comments-clip'
    ) as HTMLElement | null;
    const fold = comments?.querySelector(
      '.eazix-exhibition-comments-fold'
    ) as HTMLElement | null;
    const compose = panel.querySelector(
      '.eazix-exhibition-compose-dock'
    ) as HTMLElement | null;
    const editPanel = panel.querySelector(
      '.personal-wall-edit-panel'
    ) as HTMLElement | null;
    let h = 0;
    if (media) {
      h += media.offsetHeight;
    }
    if (editPanel) {
      h += editPanel.offsetHeight;
    } else if (fold) {
      h += fold.scrollHeight;
    } else if (comments) {
      h += comments.scrollHeight;
    }
    if (compose && !editPanel) {
      h += compose.offsetHeight;
    }
    return h > 0 ? h : panel.scrollHeight;
  }

  private syncFocusFillFromOverflow(cardId: string, force = false): void {
    const panel = this.getFocusPanelEl(cardId);
    if (!panel || typeof window === 'undefined') {
      return;
    }
    if (!force && Date.now() < this.focusFlipUntil) {
      return;
    }
    const maxH = this.focusDialogMaxHeight();
    const item = this.publishedItems.find((c) => c._id === cardId);
    const contentH = this.intrinsicFocusContentHeight(panel);
    if (contentH > maxH + 1) {
      this.focusCappedIds.add(cardId);
      panel.classList.add('personal-wall-card-focus-fill');
    } else if (!item?.commentsLoading) {
      this.focusCappedIds.delete(cardId);
      panel.classList.remove('personal-wall-card-focus-fill');
    }
    this.clearFocusScrollInline(panel);
  }

  /** Drop leftover inline overflow/padding from compose scroll helpers. */
  private clearFocusScrollInline(panel: HTMLElement, resetScrollTop = false): void {
    const scroll = panel.querySelector(
      '.wall-card-social-scroll'
    ) as HTMLElement | null;
    if (!scroll) {
      return;
    }
    // Keep overflow-y under CSS (hidden scrollbar). Only clear sticky padding.
    scroll.style.overflowY = '';
    scroll.style.paddingBottom = '';
    if (resetScrollTop) {
      scroll.scrollTop = 0;
    }
  }

  onPublishedCommentsLayout(item: WallPublishedViewItem): void {
    if (!this.isPublishedFocus(item) || !item.commentsOpen) {
      return;
    }
    this.scheduleFocusAdapt();
  }

  private scheduleFocusAdapt(): void {
    const id = this.commentsTargetId;
    if (!id) {
      return;
    }
    if (Date.now() < this.focusFlipUntil) {
      this.focusAdaptQueued = true;
      return;
    }
    if (this.focusAdaptRaf != null) {
      cancelAnimationFrame(this.focusAdaptRaf);
    }
    this.focusAdaptRaf = requestAnimationFrame(() => {
      this.focusAdaptRaf = null;
      this.ngZone.run(() => {
        const cardId = this.commentsTargetId;
        if (!cardId) {
          return;
        }
        const item = this.publishedItems.find((c) => c._id === cardId);
        if (!item?.commentsOpen) {
          return;
        }
        this.cdr.detectChanges();
        if (this.publishedFocusMode === 'edit') {
          this.relayoutFocusAfterEditMode(cardId);
          return;
        }
        if (this.isFocusPanelPinned(cardId)) {
          this.syncFocusPanelPinnedHeight(cardId);
          return;
        }
        this.quietAdaptFocusHeight(cardId);
      });
    });
  }

  /** Keep the fixed panel within the viewport; extra comments scroll inside. */
  private relayoutFocusPanel(cardId: string, force = false): void {
    const panel = this.getFocusPanelEl(cardId);
    if (!panel || typeof window === 'undefined') {
      return;
    }
    if (!force && Date.now() < this.focusFlipUntil) {
      return;
    }
    const finalW = this.focusDialogWidth(cardId);
    const maxH = this.focusDialogMaxHeight();
    panel.style.transition = 'none';
    panel.style.transform = 'none';
    panel.style.width = `${finalW}px`;
    panel.style.maxWidth = `${finalW}px`;
    panel.style.maxHeight = `${maxH}px`;
    const measured = panel.getBoundingClientRect();
    const finalH = Math.min(Math.max(1, measured.height), maxH);
    panel.style.left = `${this.focusDialogLeft(finalW)}px`;
    panel.style.top = `${this.focusDialogTop(finalH)}px`;
  }

  private playFocusFlipOpen(cardId: string): void {
    const panel = this.getFocusPanelEl(cardId);
    const origin = this.focusFlipOrigin;
    if (!panel || !origin || typeof window === 'undefined') {
      return;
    }
    if (this.focusCappedIds.has(cardId)) {
      panel.classList.add('personal-wall-card-focus-fill');
    } else {
      panel.classList.remove('personal-wall-card-focus-fill');
    }
    // Neutralize CSS centering transform so the measured final box matches pin geometry.
    panel.style.transition = 'none';
    panel.style.transform = 'none';
    void panel.offsetWidth;
    const panelLast = panel.getBoundingClientRect();
    const media = wallFocusMediaEl(panel);
    const mediaLast = media?.getBoundingClientRect() ?? null;
    this.focusFlipUntil = Date.now() + this.commentsAnimMs;
    const played = wallFocusPlayOpenFromSlot(
      panel,
      origin,
      this.commentsAnimMs,
      'personal-wall-card-focus-opening',
      panelLast,
      mediaLast,
      { slotRadius: '0', openRadius: '0' }
    );
    if (!played) {
      // Fallback: stay at pinned final geometry.
      panel.classList.remove('personal-wall-card-focus-opening');
    }
  }

  private playFocusFlipClose(
    cardId: string,
    mediaStart?: DOMRect | null,
    panelStart?: DOMRect | null
  ): boolean {
    const panel = this.getFocusPanelEl(cardId);
    const origin = this.focusFlipOrigin;
    if (!panel || !origin) {
      return false;
    }
    return wallFocusPlayCloseToSlot(
      panel,
      origin,
      this.commentsAnimMs,
      'personal-wall-card-focus-returning',
      mediaStart,
      panelStart,
      { slotRadius: '0', openRadius: '0' }
    );
  }

  private lockBodyScroll(): void {
    if (typeof document === 'undefined' || this.bodyOverflowPrev != null) {
      return;
    }
    this.bodyOverflowPrev = document.body.style.overflow;
    this.docOverflowPrev = document.documentElement.style.overflow;
    document.body.style.overflow = 'hidden';
    document.documentElement.style.overflow = 'hidden';
    const scroller = wallScrollParent(this.host.nativeElement);
    if (
      scroller &&
      scroller !== document.body &&
      scroller !== document.documentElement
    ) {
      this.wallScrollerEl = scroller;
      this.wallScrollerOverflowPrev = scroller.style.overflow;
      scroller.style.overflow = 'hidden';
    }
  }

  private unlockBodyScroll(): void {
    if (typeof document === 'undefined' || this.bodyOverflowPrev == null) {
      return;
    }
    document.body.style.overflow = this.bodyOverflowPrev;
    document.documentElement.style.overflow = this.docOverflowPrev ?? '';
    this.bodyOverflowPrev = null;
    this.docOverflowPrev = null;
    if (this.wallScrollerEl) {
      this.wallScrollerEl.style.overflow = this.wallScrollerOverflowPrev ?? '';
      this.wallScrollerEl = null;
      this.wallScrollerOverflowPrev = null;
    }
  }

  startEditPublishedTitle(item: WallPublishedViewItem, event?: Event): void {
    event?.stopPropagation();
    item.editingTitle = true;
    setTimeout(() => this.fitPublishedEditFields(item, 'title'), 0);
  }

  startEditPublishedDescription(item: WallPublishedViewItem, event?: Event): void {
    event?.stopPropagation();
    item.editingDescription = true;
    setTimeout(() => this.fitPublishedEditFields(item, 'description'), 0);
  }

  resizeWorkField(event: Event): void {
    const el = event.target as HTMLTextAreaElement | null;
    if (el) {
      this.fitWorkTextarea(el);
    }
    this.persistDraftSessionState();
  }

  /** Edit-focus field input: grow textarea + keep save button at the bottom. */
  onPublishedEditFieldInput(item: WallPublishedViewItem, event: Event): void {
    const el = event.target as HTMLTextAreaElement | null;
    if (el) {
      this.fitWorkTextarea(el);
      if (el.getAttribute('data-work-title') === item._id) {
        item.draftTitle = el.value;
      } else if (el.getAttribute('data-work-desc') === item._id) {
        item.draftDescription = el.value;
      }
    }
    if (!this.isPublishedEditFocus(item)) {
      return;
    }
    this.cdr.detectChanges();
    this.relayoutFocusAfterEditMode(item._id, this.isPublishedDirty(item));
  }

  private fitPublishedEditFields(
    item: WallPublishedViewItem,
    field?: 'title' | 'description'
  ): void {
    if (!field || field === 'title') {
      const title = document.querySelector(
        `[data-work-title="${item._id}"]`
      ) as HTMLTextAreaElement | null;
      if (title) {
        this.fitWorkTextarea(title);
      }
    }
    if (!field || field === 'description') {
      const desc = document.querySelector(
        `[data-work-desc="${item._id}"]`
      ) as HTMLTextAreaElement | null;
      if (desc) {
        this.fitWorkTextarea(desc);
      }
    }
  }

  private fitWorkTextarea(el: HTMLTextAreaElement): void {
    el.style.height = 'auto';
    el.style.overflow = 'hidden';
    el.style.height = `${el.scrollHeight}px`;
  }

  cancelEditPublishedTitle(item: WallPublishedViewItem, event?: Event): void {
    event?.preventDefault();
    event?.stopPropagation();
    if (!item || item.savingTitle) {
      return;
    }
    item.draftTitle = item.baselineTitle || '';
    item.editingTitle = false;
  }

  cancelEditPublishedDescription(item: WallPublishedViewItem, event?: Event): void {
    event?.preventDefault();
    event?.stopPropagation();
    if (!item || item.savingDescription) {
      return;
    }
    item.draftDescription = item.baselineDescription || '';
    item.editingDescription = false;
  }

  clearPublishedTitle(item: WallPublishedViewItem, event?: Event): void {
    event?.stopPropagation();
    item.draftTitle = '';
    item.editingTitle = true;
    setTimeout(() => this.fitPublishedEditFields(item, 'title'), 0);
  }

  clearPublishedDescription(item: WallPublishedViewItem, event?: Event): void {
    event?.stopPropagation();
    item.draftDescription = '';
    item.editingDescription = true;
    setTimeout(() => this.fitPublishedEditFields(item, 'description'), 0);
  }

  isPublishedTitleDirty(item: WallPublishedViewItem): boolean {
    return String(item.draftTitle || '') !== String(item.baselineTitle || '');
  }

  isPublishedDescriptionDirty(item: WallPublishedViewItem): boolean {
    return String(item.draftDescription || '') !== String(item.baselineDescription || '');
  }

  isPublishedDirty(item: WallPublishedViewItem): boolean {
    return this.isPublishedTitleDirty(item) || this.isPublishedDescriptionDirty(item);
  }

  isPublishedSaving(item: WallPublishedViewItem): boolean {
    return !!(item.savingTitle || item.savingDescription);
  }

  /**
   * Toggle whether this work appears in the public exhibition.
   * Optimistic update; reverts on failure.
   */
  toggleHiddenFromAudience(item: WallPublishedViewItem, event?: Event): void {
    event?.preventDefault();
    event?.stopPropagation();
    this.blurWorkActionTarget(event);
    if (!item?._id || item.deleting || item.hiding) {
      return;
    }
    const prev = item.hiddenFromAudience === true;
    const next = !prev;
    item.hiddenFromAudience = next;
    item.hiding = true;

    const gen = (this.hideItemGen[item._id] || 0) + 1;
    this.hideItemGen[item._id] = gen;
    this.hideItemSubs[item._id]?.unsubscribe();
    this.hideItemSubs[item._id] = this.wallApiService
      .updateItem(item._id, { hiddenFromAudience: next })
      .subscribe({
        next: (res) => {
          if (this.hideItemGen[item._id] !== gen) {
            return;
          }
          item.hiddenFromAudience = res?.item?.hiddenFromAudience === true;
          item.hiding = false;
          delete this.hideItemSubs[item._id];
          if (prev && !item.hiddenFromAudience && !this.isPublishedFocus(item)) {
            this.showSnack('wall.personal.shown-to-audience');
          }
        },
        error: () => {
          if (this.hideItemGen[item._id] !== gen) {
            return;
          }
          item.hiddenFromAudience = prev;
          item.hiding = false;
          delete this.hideItemSubs[item._id];
          this.showSnack('wall.personal.save-error');
        },
      });
  }

  deletePublishedWork(item: WallPublishedViewItem, event?: Event): void {
    event?.preventDefault();
    event?.stopPropagation();
    if (!item?._id || item.deleting) {
      return;
    }
    item.deleting = true;
    this.deleteItemSubs[item._id]?.unsubscribe();
    this.deleteItemSubs[item._id] = this.wallApiService
      .deleteItem(item._id)
      .subscribe({
        next: () => {
          this.publishedItems = this.publishedItems.filter(
            (p) => p._id !== item._id
          );
          this.setPublishedTabCount(this.publishedItems.length);
          this.clampPublishedVisible();
          this.applyPublishedRowsLayout(true);
          delete this.deleteItemSubs[item._id];
          this.saveItemSubs[this.saveSubKey(item._id, 'title')]?.unsubscribe();
          delete this.saveItemSubs[this.saveSubKey(item._id, 'title')];
          this.saveItemSubs[this.saveSubKey(item._id, 'description')]?.unsubscribe();
          delete this.saveItemSubs[this.saveSubKey(item._id, 'description')];
          this.saveItemSubs[this.saveSubKey(item._id, 'all')]?.unsubscribe();
          delete this.saveItemSubs[this.saveSubKey(item._id, 'all')];
          this.rotateItemSubs[item._id]?.unsubscribe();
          delete this.rotateItemSubs[item._id];
          delete this.rotateItemGen[item._id];
          this.hideItemSubs[item._id]?.unsubscribe();
          delete this.hideItemSubs[item._id];
          delete this.hideItemGen[item._id];
          this.showSnack('wall.personal.delete-success');
          this.loadUnsharedInitial();
        },
        error: () => {
          item.deleting = false;
          this.showSnack('wall.personal.delete-error');
        },
      });
  }

  saveAllPublishedChanges(item: WallPublishedViewItem): void {
    if (!item?._id || this.isPublishedSaving(item) || !this.isPublishedDirty(item)) {
      return;
    }
    const titleDirty = this.isPublishedTitleDirty(item);
    const descriptionDirty = this.isPublishedDescriptionDirty(item);
    const titleValue = item.draftTitle || '';
    const descriptionValue = item.draftDescription || '';
    const payload: { title?: string; description?: string } = {};
    if (titleDirty) {
      payload.title = titleValue;
      item.savingTitle = true;
    }
    if (descriptionDirty) {
      payload.description = descriptionValue;
      item.savingDescription = true;
    }
    const subKey = this.saveSubKey(item._id, 'all');
    this.saveItemSubs[subKey]?.unsubscribe();
    this.saveItemSubs[this.saveSubKey(item._id, 'title')]?.unsubscribe();
    this.saveItemSubs[this.saveSubKey(item._id, 'description')]?.unsubscribe();
    this.saveItemSubs[subKey] = this.wallApiService
      .updateItem(item._id, payload)
      .subscribe({
        next: (res) => {
          const savedItem = res?.item;
          if (titleDirty) {
            item.title =
              savedItem?.title != null ? String(savedItem.title) : titleValue;
            item.baselineTitle = item.title || '';
            item.draftTitle = item.baselineTitle;
            item.editingTitle = false;
            item.savingTitle = false;
          }
          if (descriptionDirty) {
            item.description =
              savedItem?.description != null
                ? String(savedItem.description)
                : descriptionValue;
            item.baselineDescription = item.description || '';
            item.draftDescription = item.baselineDescription;
            item.editingDescription = false;
            item.savingDescription = false;
          }
          if (
            !this.rotateItemSubs[item._id] &&
            savedItem != null &&
            savedItem.rotation != null
          ) {
            const savedRotation = this.normalizeRotation(savedItem.rotation);
            item.rotation = savedRotation;
            item.draftRotation = savedRotation;
            item.baselineRotation = savedRotation;
          }
          delete this.saveItemSubs[subKey];
          this.showSnack('wall.personal.save-success');
          if (this.isPublishedEditFocus(item)) {
            this.cdr.detectChanges();
            this.relayoutFocusAfterEditMode(item._id);
          }
        },
        error: () => {
          item.savingTitle = false;
          item.savingDescription = false;
          this.showSnack('wall.personal.save-error');
        },
      });
  }

  savePublishedChanges(
    item: WallPublishedViewItem,
    field: 'title' | 'description'
  ): void {
    // Prefer the unified save; keep for any remaining single-field callers.
    this.saveAllPublishedChanges(item);
    void field;
  }

  private saveSubKey(
    itemId: string,
    field: 'title' | 'description' | 'all'
  ): string {
    return `${itemId}:${field}`;
  }

  clipText(value?: string, max = 20): string {
    const t = String(value || '').trim();
    if (t.length <= max) {
      return t;
    }
    return `${t.slice(0, max)}...`;
  }

  displayWorkText(value: string | undefined, _item?: WallPublishedViewItem): string {
    return String(value || '').trim();
  }

  onWorkTitleOverflow(item: WallPublishedViewItem, overflowing: boolean): void {
    item.workTitleOverflow = overflowing;
  }

  onWorkDescOverflow(item: WallPublishedViewItem, overflowing: boolean): void {
    item.workDescOverflow = overflowing;
  }

  toggleWorkText(item: WallPublishedViewItem, field: 'title' | 'description', event?: Event): void {
    event?.stopPropagation();
    if (field === 'title') {
      item.workTitleExpanded = !item.workTitleExpanded;
    } else {
      item.workDescExpanded = !item.workDescExpanded;
    }
    if (this.isPublishedFocus(item) && item.commentsOpen) {
      this.cdr.detectChanges();
      this.afterFocusContentLayout(item._id);
    }
  }

  truncateFileName(name: string, maxLen = 20): string {
    return this.clipText(name, maxLen);
  }

  private nextRotation(current: number | undefined): 0 | 90 | 180 | 270 {
    const deg = this.normalizeRotation(current);
    return ((deg + 90) % 360) as 0 | 90 | 180 | 270;
  }

  private normalizeRotation(value: number | undefined): 0 | 90 | 180 | 270 {
    const n = Number(value);
    if (n === 90 || n === 180 || n === 270) {
      return n;
    }
    return 0;
  }

  onProfileImageError(): void {
    this.profileImgCandidates = this.profileImgCandidates.slice(1);
    this.userProfileImg = this.profileImgCandidates[0] || null;
  }

  publishedThumb(item: WallPublishedItem): string {
    return resolveWallItemThumb(item);
  }

  trackPublishedById(_index: number, item: WallPublishedViewItem): string {
    return item?._id || String(_index);
  }

  trackDraftById(_index: number, file: WallFileItem): string {
    return file?.id || String(_index);
  }

  /** Discard "which works to share?" draft and return to normal wall view. */
  cancelDraftShare(): void {
    if (this.isPublishing || this.isDraftCollapsing || !this.wallFiles.length) {
      return;
    }
    this.sessionMeta = null;
    this.sessionSignature = this.buildSessionSignature([], null);
    // Mark collapsing before clear so sessionFiles$ won't remount an empty draft.
    this.isDraftCollapsing = true;
    this.wallSessionService.clearWallSessionFiles();
    this.startDraftCollapse();
  }

  publishWorks(): void {
    if (!this.hasSelectedPages || this.isPublishing || this.isDraftCollapsing) {
      return;
    }

    const printingService = String(this.sessionMeta?.printingService || '').toLowerCase();
    const serialName = String(this.sessionMeta?.serialName || '').trim();
    if (
      (printingService !== 'plotter' && printingService !== 'express') ||
      !serialName
    ) {
      this.showSnack('wall.personal.publish-missing-meta');
      return;
    }

    const items: WallPublishItemPayload[] = [];
    for (const file of this.wallFiles) {
      if (!file.fileId || !file.included || isWallPlaceholderThumb(file.thumbUrl)) {
        continue;
      }
      items.push({
        fileId: file.fileId,
        printingService: printingService as 'plotter' | 'express',
        page: file.page,
        serialName,
        title: file.title || '',
        description: file.description || '',
        fileName: file.fileName || '',
        rotation: file.rotation || 0,
        hiddenFromAudience: file.hiddenFromAudience === true,
      });
    }

    if (!items.length) {
      this.showSnack('wall.personal.publish-missing-meta');
      return;
    }

    const allHidden = items.every((item) => item.hiddenFromAudience === true);

    this.isPublishing = true;
    this.pendingHighlightKeys = new Set(
      items.map((item) => `${item.fileId}:${item.page}`)
    );
    this.publishSub = this.wallApiService.publishWorks(items).subscribe({
      next: () => {
        this.wallSessionService.clearWallSessionFiles();
        this.sessionMeta = null;
        this.sessionSignature = '';
        this.authService.setHaveWall(true);
        this.showSnack(
          allHidden
            ? 'wall.personal.publish-hidden-success'
            : 'wall.personal.publish-success'
        );
        this.isPublishing = false;
        this.startDraftCollapse();
        this.fetchPublishedWorks(false, () => this.loadUnsharedInitial());
      },
      error: () => {
        this.isPublishing = false;
        this.pendingHighlightKeys.clear();
        this.showSnack('wall.personal.publish-error');
      },
    });
  }

  isJustAdded(item: { _id?: string } | null | undefined): boolean {
    return !!item?._id && this.justAddedIds.has(String(item._id));
  }

  onDraftCollapseEnd(event: TransitionEvent): void {
    if (!this.isDraftCollapsing) {
      return;
    }
    if (event.target !== event.currentTarget) {
      return;
    }
    if (
      event.propertyName !== 'max-height' &&
      event.propertyName !== 'opacity'
    ) {
      return;
    }
    this.finishDraftCollapse();
  }

  private startDraftCollapse(): void {
    if (this.draftCollapseTimer) {
      clearTimeout(this.draftCollapseTimer);
      this.draftCollapseTimer = null;
    }
    if (!this.wallFiles.length) {
      this.finishDraftCollapse();
      return;
    }
    // Next frame so the browser applies the collapsing transition from full height
    requestAnimationFrame(() => {
      this.isDraftCollapsing = true;
      this.draftCollapseTimer = setTimeout(() => {
        this.finishDraftCollapse();
      }, 520);
    });
  }

  private finishDraftCollapse(): void {
    if (this.draftCollapseTimer) {
      clearTimeout(this.draftCollapseTimer);
      this.draftCollapseTimer = null;
    }
    this.isDraftCollapsing = false;
    this.wallFiles = [];
    if (this.queuedJustAddedIds.length) {
      this.activateJustAddedGlow(this.queuedJustAddedIds);
    }
  }

  private queueJustAddedFromPublished(): void {
    if (!this.pendingHighlightKeys.size) {
      return;
    }
    const matched = this.publishedItems
      .filter((item) =>
        this.pendingHighlightKeys.has(`${item.fileId}:${item.page}`)
      )
      .map((item) => String(item._id));
    this.pendingHighlightKeys.clear();
    if (!matched.length) {
      this.justAddedPreferImmediate = false;
      return;
    }
    // Wait for draft collapse to finish so the glow lands on the final layout
    if (
      !this.justAddedPreferImmediate &&
      (this.isDraftCollapsing || this.wallFiles.length)
    ) {
      this.queuedJustAddedIds = matched;
      return;
    }
    this.justAddedPreferImmediate = false;
    this.activateJustAddedGlow(matched);
  }

  private activateJustAddedGlow(ids: string[]): void {
    this.queuedJustAddedIds = [];
    const cleanIds = ids.filter(Boolean);
    this.justAddedIds = new Set(cleanIds);
    if (this.justAddedClearTimer) {
      clearTimeout(this.justAddedClearTimer);
    }
    this.justAddedClearTimer = setTimeout(() => {
      this.justAddedIds = new Set();
      this.justAddedClearTimer = null;
    }, 2600);

    const firstId = cleanIds[0];
    if (!firstId) {
      return;
    }
    const idx = this.publishedItems.findIndex(
      (item) => String(item._id) === firstId
    );
    if (idx >= 0) {
      this.publishedVisibleCount = Math.max(
        this.publishedVisibleCount,
        Math.min(this.publishedItems.length, idx + 1)
      );
      this.applyPublishedRowsLayout(true);
    }
    this.cdr.detectChanges();
    setTimeout(() => this.scrollPublishedCardIntoViewFast(firstId), 50);
  }

  private startWallPolling(): void {
    this.wallPollSub?.unsubscribe();
    this.wallPollSub = interval(10000)
      .pipe(switchMap(() => this.wallApiService.getMyWall()))
      .subscribe({
        next: (res) => this.applyPublishedWall(res?.wall),
        error: () => {
          /* keep previous published items on poll failure */
        },
      });
  }

  private fetchPublishedWorks(showSpinner = false, onDone?: () => void): void {
    this.wallFetchSub?.unsubscribe();
    if (showSpinner) {
      this.isLoadingPublished = true;
      this.setPublishedSkeletons(true);
    }
    this.wallFetchSub = this.wallApiService.getMyWall().subscribe({
      next: (res) => {
        this.applyPublishedWall(res?.wall, {
          onDone,
        });
      },
      error: () => {
        this.clearPublishedSkeletons();
        this.isLoadingPublished = false;
        onDone?.();
      },
    });
  }

  private applyPublishedWall(
    wall:
      | {
          about?: string;
          lookingForWork?: boolean;
          offeringHelp?: boolean;
          allowContact?: boolean;
          academySerialName?: string;
          items?: WallPublishedItem[];
        }
      | null
      | undefined,
    opts?: { onDone?: () => void }
  ): void {
    if (!(this.editingAbout || this.isAboutDirty() || this.savingProfile)) {
      const about = wall?.about != null ? String(wall.about) : '';
      this.baselineAbout = about;
      this.draftAbout = about;
      this.editingAbout = false;
    }

    if (!this.savingStatus && !this.savingAcademy) {
      this.lookingForWork = wall?.lookingForWork === true;
      this.offeringHelp = wall?.offeringHelp === true;
      this.allowContact = wall?.allowContact !== false;
      this.academySerialName =
        wall?.academySerialName != null
          ? (() => {
              const sn = String(wall.academySerialName).trim();
              return this.isPrintingServiceSerial(sn) ? '' : sn;
            })()
          : '';
      this.academyIsSuggested = false;
      this.ensureSelectedAcademyInOptions();
      this.loadAcademyOptions();
    }
    this.ensureAcademyFromOrderSession();
    this.wallLoaded = true;
    this.wallHasItems = Array.isArray(wall?.items) && wall.items.length > 0;
    this.applySuggestedAcademy();

    const incoming = (Array.isArray(wall?.items) ? wall.items : [])
      .filter((item) => !!item?._id && !isWallPlaceholderThumb(resolveWallItemThumb(item)))
      .sort((a, b) => this.compareCreatedDesc(a, b));
    const previousById = new Map(
      this.publishedItems.map((item) => [String(item._id), item])
    );

    const nextItems = incoming.map((item) => {
      const prev = previousById.get(String(item._id));
      if (prev) {
        return this.mergePublishedServerItem(prev, item);
      }
      return this.toPublishedViewItem(item);
    });
    const pendingLocals = this.publishedItems.filter(
      (p) =>
        p.pendingShareFromUnshared &&
        !nextItems.some(
          (n) =>
            String(n.fileId) === String(p.fileId) &&
            Number(n.page) === Number(p.page)
        )
    );
    const hydrateGen = ++this.publishedHydrateGen;
    const commit = (survivors: WallPublishedViewItem[]) => {
      if (hydrateGen !== this.publishedHydrateGen) {
        return;
      }
      this.publishedItems = [...pendingLocals, ...survivors];
      this.clampPublishedVisible();
      // Only after every thumb was probed and failures dropped.
      this.setPublishedTabCount(survivors.length);
      this.clearPublishedSkeletons();
      this.applyPublishedRowsLayout(true);
      this.queueJustAddedFromPublished();
      // Clear after collage is ready (incl. image size hydrate) — never flash empty mid-load.
      this.isLoadingPublished = false;
      opts?.onDone?.();
      this.cdr.markForCheck();
      setTimeout(() => {
        this.setupPublishedObserver();
        this.queuePublishedCatchUp();
        this.tryOpenPendingDirectShare();
      }, 0);
    };
    // Probe all thumbs first; count = how many survive.
    filterWallItemsWithLoadableThumbs(nextItems).then((survivors) => {
      this.ngZone.run(() => commit(survivors));
    });
  }

  /** Reuse the existing card instance so polls don't remount images / flash tab counts. */
  private mergePublishedServerItem(
    prev: WallPublishedViewItem,
    item: WallPublishedItem
  ): WallPublishedViewItem {
    const busy =
      this.isPublishedDirty(prev) ||
      prev.editingTitle ||
      prev.editingDescription ||
      this.isPublishedSaving(prev) ||
      prev.deleting ||
      prev.commentsOpen ||
      prev.commentsMounted ||
      !!this.rotateItemSubs[prev._id];
    const thumb = resolveWallItemThumb(item) || prev.thumbUrl;
    // Avoid reassigning the same URL (triggers img reload → hide races → badge flicker).
    if (thumb && thumb !== prev.thumbUrl) {
      prev.thumbUrl = thumb;
      prev.natW = undefined;
      prev.natH = undefined;
      prev.imgLoaded = false;
    }
    prev.serverAddress = item.serverAddress || prev.serverAddress;
    prev.fileName = item.fileName || prev.fileName;
    prev.fileId = item.fileId || prev.fileId;
    prev.page = item.page ?? prev.page;
    prev.printingService = item.printingService || prev.printingService;
    prev.serialName = item.serialName || prev.serialName;
    if (busy) {
      return prev;
    }
    const title = item.title != null ? String(item.title) : '';
    const description =
      item.description != null ? String(item.description) : '';
    const rotation = this.normalizeRotation(item.rotation);
    prev.title = title;
    prev.description = description;
    prev.rotation = rotation;
    prev.baselineTitle = title;
    prev.baselineDescription = description;
    prev.baselineRotation = rotation;
    prev.draftTitle = title;
    prev.draftDescription = description;
    prev.draftRotation = rotation;
    prev.likeCount = Number(item.likeCount) || 0;
    prev.commentCount = Number(item.commentCount) || 0;
    prev.commenterCount = Number(item.commenterCount) || 0;
    prev.likedByMe = item.likedByMe === true;
    prev.recentLikers = Array.isArray(item.recentLikers)
      ? item.recentLikers
      : [];
    prev.recentCommenters = Array.isArray(item.recentCommenters)
      ? item.recentCommenters
      : [];
    if (!prev.likersExpanded) {
      prev.likers = Array.isArray(item.recentLikers)
        ? [...item.recentLikers]
        : [];
    }
    prev.hiddenFromAudience = item.hiddenFromAudience === true;
    return prev;
  }

  private toPublishedViewItem(item: WallPublishedItem): WallPublishedViewItem {
    const title = item.title != null ? String(item.title) : '';
    const description =
      item.description != null ? String(item.description) : '';
    const rotation = this.normalizeRotation(item.rotation);
    return {
      ...item,
      thumbUrl: resolveWallItemThumb(item),
      title,
      description,
      rotation,
      likeCount: Number(item.likeCount) || 0,
      commentCount: Number(item.commentCount) || 0,
      commenterCount: Number(item.commenterCount) || 0,
      likedByMe: item.likedByMe === true,
      recentLikers: Array.isArray(item.recentLikers) ? item.recentLikers : [],
      recentCommenters: Array.isArray(item.recentCommenters)
        ? item.recentCommenters
        : [],
      likers: Array.isArray(item.recentLikers) ? [...item.recentLikers] : [],
      likersExpanded: false,
      commentsOpen: false,
      commentsMounted: false,
      commentsLoading: false,
      comments: [],
      draftComment: '',
      liking: false,
      commenting: false,
      editingTitle: false,
      editingDescription: false,
      savingTitle: false,
      savingDescription: false,
      deleting: false,
      hiding: false,
      draftTitle: title,
      draftDescription: description,
      draftRotation: rotation,
      baselineTitle: title,
      baselineDescription: description,
      baselineRotation: rotation,
      hiddenFromAudience: item.hiddenFromAudience === true,
      workTitleExpanded: false,
      workDescExpanded: false,
      imgLoaded: false,
    };
  }

  private showSnack(key: string): void {
    this.translate.get(key).subscribe((msg) => {
      this.snackBar.open(msg, '', {
        duration: 3500,
        verticalPosition:
          key === 'wall.personal.save-success' && !isWallDesktopViewport()
            ? 'top'
            : 'bottom',
      });
    });
  }

  trackRow(index: number): number {
    return index;
  }

  trackLayoutItem(
    _index: number,
    item: { work?: { _id?: string; id?: string } }
  ): string {
    return item?.work?._id || item?.work?.id || String(_index);
  }

  isSkeletonWork(work: unknown): work is WallSkeletonWork {
    return isWallSkeletonWork(work);
  }

  private measureCollageWidth(el?: HTMLElement | null): number {
    const readContentWidth = (node: HTMLElement): number => {
      const style = window.getComputedStyle(node);
      const padL = parseFloat(style.paddingLeft) || 0;
      const padR = parseFloat(style.paddingRight) || 0;
      return Math.max(0, node.clientWidth - padL - padR);
    };
    if (el?.clientWidth) {
      return readContentWidth(el);
    }
    const main = this.host.nativeElement.querySelector(
      '.personal-wall-main'
    ) as HTMLElement | null;
    if (main?.clientWidth) {
      return readContentWidth(main);
    }
    if (typeof window !== 'undefined') {
      return Math.min(window.innerWidth, 1200);
    }
    return 720;
  }

  private setPublishedSkeletons(initial: boolean): void {
    const width = this.measureCollageWidth(
      this.publishedCollageRef?.nativeElement
    );
    const count = wallSkeletonCount(width, initial ? 2 : 1);
    this.publishedSkeletonSlots = makeWallSkeletonWorks(
      count,
      initial ? 'pw-pub-skel' : 'pw-pub-skel-more',
      initial ? undefined : this.pagedPublishedItems
    );
    this.cdr.detectChanges();
    this.applyPublishedRowsLayout(true);
  }

  private clearPublishedSkeletons(): void {
    if (!this.publishedSkeletonSlots.length) {
      return;
    }
    this.publishedSkeletonSlots = [];
  }

  private setUnsharedSkeletons(initial: boolean): void {
    const width = this.measureCollageWidth(
      this.unsharedCollageRef?.nativeElement
    );
    const count = wallSkeletonCount(width, initial ? 2 : 1);
    const source = this.unsharedItems.map((u) => ({
      natW: u.natW,
      natH: u.natH,
    }));
    this.unsharedSkeletonSlots = makeWallSkeletonWorks(
      count,
      initial ? 'pw-un-skel' : 'pw-un-skel-more',
      initial ? undefined : source
    );
    this.cdr.detectChanges();
    this.applyUnsharedRowsLayout(true);
  }

  private clearUnsharedSkeletons(): void {
    if (!this.unsharedSkeletonSlots.length) {
      return;
    }
    this.unsharedSkeletonSlots = [];
  }

  /** After draft grid mounts, rebind observers for published/unshared collages. */
  private scheduleDraftMount(): void {
    setTimeout(() => {
      this.bindLayoutObserver();
      this.scheduleWorksTabSizeSync();
      this.cdr.markForCheck();
    }, 0);
  }

  private applyPublishedRowsLayout(force = false): void {
    const width = this.measureCollageWidth(
      this.publishedCollageRef?.nativeElement
    );
    if (!force && this.lastPublishedLayoutWidth > 0) {
      const delta = Math.abs(width - this.lastPublishedLayoutWidth);
      if (
        delta < GALLERY_ROWS_TUNABLES.resizeThresholdPx &&
        this.publishedLayoutRows.length
      ) {
        return;
      }
    }
    const page =
      this.worksTab === 'published'
        ? this.pagedPublishedItems
        : this.publishedItems.filter((p) => !!p.pendingShareFromUnshared);
    const skeletons =
      this.worksTab === 'published' ? this.publishedSkeletonSlots : [];
    if (!page.length && !skeletons.length) {
      this.publishedLayoutRows = [];
      this.publishedLayoutTotalHeight = 0;
      this.lastPublishedLayoutWidth = width;
      return;
    }
    const inputs: Array<WallPublishedViewItem | WallSkeletonWork> = [
      ...page.map((p) => Object.assign(p, { rotation: p.draftRotation })),
      ...skeletons,
    ];
    const result = this.rowsLayout.layout(inputs, width);
    const byId = new Map(page.map((p) => [String(p._id), p]));
    this.publishedLayoutRows = result.rows.map((row) => ({
      ...row,
      items: row.items.map((item) => ({
        ...item,
        work: byId.get(String(item.work._id)) || item.work,
      })),
    }));
    this.publishedLayoutTotalHeight = result.totalHeight;
    this.publishedLayoutGap = result.gap;
    this.lastPublishedLayoutWidth = width;
  }

  private applyUnsharedRowsLayout(force = false): void {
    const width = this.measureCollageWidth(
      this.unsharedCollageRef?.nativeElement
    );
    if (!force && this.lastUnsharedLayoutWidth > 0) {
      const delta = Math.abs(width - this.lastUnsharedLayoutWidth);
      if (
        delta < GALLERY_ROWS_TUNABLES.resizeThresholdPx &&
        this.unsharedLayoutRows.length
      ) {
        return;
      }
    }
    type UnsharedWork = WallUnsharedViewItem & { _id: string };
    const live: UnsharedWork[] = this.unsharedItems.map((u) =>
      Object.assign(u, { _id: u.id })
    );
    const works: Array<UnsharedWork | WallSkeletonWork> = [
      ...live,
      ...this.unsharedSkeletonSlots,
    ];
    if (!works.length) {
      this.unsharedLayoutRows = [];
      this.unsharedLayoutTotalHeight = 0;
      this.lastUnsharedLayoutWidth = width;
      return;
    }
    const result = this.rowsLayout.layout(works, width);
    const byId = new Map(this.unsharedItems.map((u) => [u.id, u]));
    this.unsharedLayoutRows = result.rows.map((row) => ({
      ...row,
      items: row.items.map((item) => ({
        ...item,
        work: (byId.get(String(item.work._id)) ||
          item.work) as (WallUnsharedViewItem & { _id: string }) | WallSkeletonWork,
      })),
    }));
    this.unsharedLayoutTotalHeight = result.totalHeight;
    this.unsharedLayoutGap = result.gap;
    this.lastUnsharedLayoutWidth = width;
  }

  private bindLayoutObserver(): void {
    this.unbindLayoutObserver();
    if (typeof ResizeObserver === 'undefined') {
      return;
    }
    this.layoutResizeObserver = new ResizeObserver(() => {
      this.scheduleRowsRelayoutFromResize();
      this.scheduleWorksTabSizeSync();
    });
    const observe = (el?: HTMLElement | null) => {
      if (el) {
        this.layoutResizeObserver?.observe(el);
      }
    };
    observe(this.publishedCollageRef?.nativeElement);
    observe(this.unsharedCollageRef?.nativeElement);
    const main = this.host.nativeElement.querySelector(
      '.personal-wall-main'
    ) as HTMLElement | null;
    observe(main);
    const tabs = this.host.nativeElement.querySelector(
      '.personal-wall-works-tabs'
    ) as HTMLElement | null;
    observe(tabs);
  }

  private unbindLayoutObserver(): void {
    this.layoutResizeObserver?.disconnect();
    this.layoutResizeObserver = null;
  }

  private scheduleWorksTabSizeSync(): void {
    if (typeof window === 'undefined') {
      return;
    }
    if (this.worksTabSizeRaf != null) {
      cancelAnimationFrame(this.worksTabSizeRaf);
    }
    this.worksTabSizeRaf = requestAnimationFrame(() => {
      this.worksTabSizeRaf = null;
      this.syncWorksTabSizes();
    });
  }

  /**
   * Size each tab to its text, then share leftover row width equally —
   * at most {@link worksTabMaxGrowPx} per tab.
   * Growth applies on SM+ (≥576px) only; below SM tabs stay text-sized.
   */
  private syncWorksTabSizes(): void {
    const row = this.host.nativeElement.querySelector(
      '.personal-wall-works-tabs'
    ) as HTMLElement | null;
    if (!row) {
      return;
    }
    const tabs = Array.from(
      row.querySelectorAll(':scope > .personal-wall-works-tab')
    ) as HTMLElement[];
    if (!tabs.length) {
      return;
    }

    for (const tab of tabs) {
      tab.style.width = 'auto';
      tab.style.maxWidth = 'none';
      tab.style.minWidth = '';
      tab.style.flex = '0 0 auto';
    }
    void row.offsetWidth;

    const isSmUp =
      typeof window !== 'undefined' && window.innerWidth >= 576;
    if (!isSmUp) {
      return;
    }

    const gap = parseFloat(getComputedStyle(row).columnGap || getComputedStyle(row).gap) || 0;
    const bases = tabs.map((tab) => tab.getBoundingClientRect().width);
    const baseTotal =
      bases.reduce((sum, w) => sum + w, 0) + gap * Math.max(0, tabs.length - 1);
    const leftover = Math.max(0, row.clientWidth - baseTotal);
    const growEach = Math.min(this.worksTabMaxGrowPx, leftover / tabs.length);

    for (let i = 0; i < tabs.length; i++) {
      tabs[i].style.width = `${Math.ceil(bases[i] + growEach)}px`;
    }
  }

  private scheduleRowsRelayoutFromResize(): void {
    if (this.resizeLayoutTimer) {
      clearTimeout(this.resizeLayoutTimer);
    }
    this.resizeLayoutTimer = setTimeout(() => {
      this.resizeLayoutTimer = null;
      this.ngZone.run(() => {
        if (this.pagedPublishedItems.length) {
          this.applyPublishedRowsLayout(false);
        }
        if (this.unsharedItems.length || this.unsharedHasMore) {
          this.applyUnsharedRowsLayout(false);
        }
      });
    }, GALLERY_ROWS_TUNABLES.resizeDebounceMs);
  }

  trackUnsharedById(_index: number, item: WallUnsharedViewItem): string {
    return item?.id || String(_index);
  }

  private loadUnsharedInitial(): void {
    this.unsharedFeedSub?.unsubscribe();
    this.isLoadingUnshared = true;
    this.isLoadingMoreUnshared = false;
    this.unsharedLoadError = false;
    // Keep previous items/count until the first page arrives (avoids tab badge jump).
    this.unsharedSeenIds.clear();
    this.unsharedHasMore = true;
    this.setUnsharedSkeletons(true);
    this.fetchUnsharedPage(true);
    setTimeout(() => this.setupUnsharedObserver(), 0);
  }

  private loadUnsharedMore(): void {
    if (
      this.isLoadingUnshared ||
      this.isLoadingMoreUnshared ||
      !this.unsharedHasMore
    ) {
      return;
    }
    this.isLoadingMoreUnshared = true;
    this.setUnsharedSkeletons(false);
    this.fetchUnsharedPage(false);
  }

  private fetchUnsharedPage(isInitial: boolean): void {
    this.unsharedFeedSub?.unsubscribe();
    this.unsharedFeedSub = this.wallApiService
      .getUnsharedPrintedFeed(
        Array.from(this.unsharedSeenIds),
        this.unsharedPageSize
      )
      .subscribe({
        next: (res) => {
          const incoming = Array.isArray(res?.items) ? res.items : [];
          const mapped: WallUnsharedViewItem[] = [];
          for (const raw of incoming) {
            const id = String(raw?.id || '').trim();
            if (!id || this.unsharedSeenIds.has(id)) {
              continue;
            }
            if (
              this.pendingShareSourceById.has(id) ||
              this.publishedItems.some(
                (p) =>
                  p.pendingShareFromUnshared &&
                  String(p.sourceUnsharedId || '') === id
              )
            ) {
              this.unsharedSeenIds.add(id);
              continue;
            }
            const thumbUrl = resolveWallItemThumb(raw);
            if (isWallPlaceholderThumb(thumbUrl)) {
              continue;
            }
            this.unsharedSeenIds.add(id);
            mapped.push({
              id,
              fileId: String(raw.fileId || ''),
              printingService:
                raw.printingService === 'express' ? 'express' : 'plotter',
              page: Number(raw.page) >= 1 ? Number(raw.page) : 1,
              serialName: String(raw.serialName || '').trim(),
              fileName: String(raw.fileName || ''),
              thumbUrl,
              serverAddress: String(raw.serverAddress || '').trim(),
              orderId: raw.orderId != null ? String(raw.orderId) : '',
              orderCreated:
                raw.orderCreated != null ? String(raw.orderCreated) : '',
              sharing: false,
              imgLoaded: false,
            });
          }
          this.unsharedHasMore = res?.hasMore === true;
          this.unsharedLoadError = false;
          if (!mapped.length && this.unsharedHasMore) {
            this.clearUnsharedSkeletons();
            this.isLoadingUnshared = false;
            this.isLoadingMoreUnshared = false;
            this.loadUnsharedMore();
            return;
          }
          const hydrateGen = ++this.unsharedHydrateGen;
          filterWallItemsWithLoadableThumbs(mapped).then((survivors) => {
            this.ngZone.run(() => {
              if (hydrateGen !== this.unsharedHydrateGen) {
                return;
              }
              this.unsharedItems = isInitial
                ? survivors
                : [...this.unsharedItems, ...survivors];
              this.setUnsharedTabCount(this.unsharedItems.length);
              this.clearUnsharedSkeletons();
              this.isLoadingUnshared = false;
              this.isLoadingMoreUnshared = false;
              this.applyUnsharedRowsLayout(true);
              setTimeout(() => {
                this.setupUnsharedObserver();
                this.queueUnsharedCatchUp();
                if (isInitial) {
                  this.tryOpenPendingDirectShare();
                }
              }, 0);
              setTimeout(() => this.queueUnsharedCatchUp(), 100);
              setTimeout(() => this.queueUnsharedCatchUp(), 400);
            });
          });
        },
        error: () => {
          this.clearUnsharedSkeletons();
          this.isLoadingUnshared = false;
          this.isLoadingMoreUnshared = false;
          this.unsharedLoadError = isInitial;
        },
      });
  }

  shareUnsharedWork(item: WallUnsharedViewItem, event?: Event): void {
    event?.preventDefault();
    event?.stopPropagation();
    if (!item?.id || item.sharing || this.isUnsharedPendingShareSlot(item)) {
      return;
    }
    if (
      !item.fileId ||
      !item.serialName ||
      isWallPlaceholderThumb(item.thumbUrl)
    ) {
      this.showSnack('wall.personal.publish-missing-meta');
      return;
    }

    // Cancel any other in-progress pending share first.
    const existingPending = this.publishedItems.filter(
      (p) => p.pendingShareFromUnshared
    );
    for (const pending of existingPending) {
      this.cancelPendingUnsharedShare(pending, { silent: true });
    }

    // Capture the live tile rect before slot-held hides the media / pending-host mounts.
    // Published focus uses the same card node; unshared animates a different panel.
    const originEl =
      (this.host.nativeElement.querySelector(
        `[data-unshared-id="${item.id}"] .personal-wall-card-media`
      ) as HTMLElement | null) ||
      (this.host.nativeElement.querySelector(
        `[data-unshared-id="${item.id}"]`
      ) as HTMLElement | null);
    const originRect = originEl?.getBoundingClientRect();
    const originOverride =
      originRect && originRect.width >= 2 && originRect.height >= 2
        ? {
            left: originRect.left,
            top: originRect.top,
            width: Math.max(1, originRect.width),
            height: Math.max(1, originRect.height),
          }
        : null;

    const pendingId = `pending-share-${item.id}`;
    const pendingCard = this.buildPendingShareFromUnshared(item, pendingId);
    this.pendingShareSourceById.set(item.id, { ...item, sharing: false });
    // Keep the unshared card in the collage as a placeholder slot (like published focus).
    item.sharing = true;

    // Stay on "עבודות שלא שותפו" until share is confirmed.
    this.publishedVisibleCount = Math.max(
      this.publishedVisibleCount,
      Math.min(this.publishedPageSize, this.publishedItems.length + 1)
    );
    this.publishedItems = [pendingCard, ...this.publishedItems];
    this.clampPublishedVisible();
    this.applyPublishedRowsLayout(true);
    this.cdr.detectChanges();

    // Open on the next frame once the pending focus panel exists.
    // Do not remeasure mid-FLIP (that was causing the unshared-only jump).
    this.ngZone.runOutsideAngular(() => {
      requestAnimationFrame(() => {
        this.ngZone.run(() => {
          if (!this.publishedItems.some((p) => p._id === pendingCard._id)) {
            return;
          }
          this.openPublishedFocus(pendingCard, 'edit', originOverride);
        });
      });
    });
  }

  /**
   * My-orders "שתף" on a page image → same pending-share edit flow as unshared tab.
   */
  private tryOpenPendingDirectShare(): void {
    if (this.pendingDirectShareConsumed || this.isLoading || this.isLoadingPublished) {
      return;
    }
    if (!this.wallSessionService.getPendingDirectShare()) {
      return;
    }
    const pending = this.wallSessionService.consumePendingDirectShare();
    this.pendingDirectShareConsumed = true;
    if (!pending?.fileId || !pending.serialName || !pending.thumbUrl) {
      return;
    }
    const alreadyShared = this.publishedItems.some(
      (p) =>
        !p.pendingShareFromUnshared &&
        String(p.fileId) === String(pending.fileId) &&
        Number(p.page) === Number(pending.page) &&
        String(p.printingService || '') === String(pending.printingService || '')
    );
    if (alreadyShared) {
      this.worksTab = 'published';
      this.showSnack('wall.personal.already-shared');
      return;
    }
    const item: WallUnsharedViewItem = {
      id: pending.id || `${pending.printingService}:${pending.fileId}:${pending.page}`,
      fileId: pending.fileId,
      printingService: pending.printingService,
      page: pending.page,
      serialName: pending.serialName,
      fileName: pending.fileName || '',
      thumbUrl: pending.thumbUrl,
      serverAddress: pending.serverAddress || '',
      orderId: pending.orderId || '',
      sharing: false,
      imgLoaded: false,
    };
    this.shareUnsharedWork(item);
  }

  isPendingUnsharedShare(item: WallPublishedViewItem | null | undefined): boolean {
    return !!item?.pendingShareFromUnshared;
  }

  /** Pending share card exists (edit overlay) — keep tab on unshared until confirm. */
  hasPendingUnsharedShareCard(): boolean {
    return this.publishedItems.some((p) => !!p.pendingShareFromUnshared);
  }

  /** Unshared gallery slot held while that work is in the share-edit focus dialog. */
  isUnsharedPendingShareSlot(item: { id?: string } | null | undefined): boolean {
    const id = String(item?.id || '');
    if (!id) {
      return false;
    }
    return this.publishedItems.some(
      (p) =>
        !!p.pendingShareFromUnshared &&
        String(p.sourceUnsharedId || '') === id
    );
  }

  /** Gallery slot narrower than 80px — compact overlays (comments). */
  isPublishedCardNarrow(width: number | null | undefined): boolean {
    const w = Number(width);
    return Number.isFinite(w) && w > 0 && w < 80;
  }

  /** Like count only when slot is wider than 55px. */
  showPublishedLikeCount(width: number | null | undefined): boolean {
    const w = Number(width);
    return Number.isFinite(w) && w > 55;
  }

  isPendingShareSubmitting(item: WallPublishedViewItem): boolean {
    return !!(item?.savingTitle || item?.savingDescription);
  }

  cancelPendingUnsharedShareClick(
    item: WallPublishedViewItem,
    event?: Event
  ): void {
    event?.preventDefault();
    event?.stopPropagation();
    if (
      !this.isPendingUnsharedShare(item) ||
      this.isPendingShareSubmitting(item)
    ) {
      return;
    }
    if (this.isPublishedFocus(item)) {
      this.closePublishedFocus(item);
      return;
    }
    this.cancelPendingUnsharedShare(item);
  }

  confirmPendingUnsharedShare(item: WallPublishedViewItem): void {
    if (
      !item?.pendingShareFromUnshared ||
      this.isPendingShareSubmitting(item) ||
      !item.fileId ||
      !item.serialName
    ) {
      return;
    }
    const sourceId = String(item.sourceUnsharedId || '');
    const hiddenFromAudience = item.hiddenFromAudience === true;
    item.savingTitle = true;
    const payload: WallPublishItemPayload = {
      fileId: item.fileId,
      printingService: item.printingService as 'plotter' | 'express',
      page: item.page,
      serialName: item.serialName,
      title: item.draftTitle || '',
      description: item.draftDescription || '',
      fileName: item.fileName || '',
      rotation: this.normalizeRotation(item.draftRotation),
      hiddenFromAudience,
    };
    this.pendingHighlightKeys = new Set([`${payload.fileId}:${payload.page}`]);
    this.justAddedPreferImmediate = true;
    this.unsharedShareSubs[sourceId || item._id]?.unsubscribe();
    this.unsharedShareSubs[sourceId || item._id] = this.wallApiService
      .publishWorks([payload])
      .subscribe({
        next: () => {
          delete this.unsharedShareSubs[sourceId || item._id];
          this.pendingShareSourceById.delete(sourceId);
          item.savingTitle = false;
          item.pendingShareFromUnshared = false;
          this.authService.setHaveWall(true);
          this.showSnack(
            hiddenFromAudience
              ? 'wall.personal.publish-hidden-success'
              : 'wall.personal.publish-success'
          );
          // Drop focus chrome without restoring the unshared card.
          clearTimeout(this.commentsCloseTimers[item._id]);
          delete this.commentsCloseTimers[item._id];
          item.commentsOpen = false;
          item.commentsMounted = false;
          if (this.commentsTargetId === item._id) {
            this.commentsTargetId = null;
            this.publishedFocusMode = null;
            this.focusFlipOrigin = null;
            delete this.focusSlotHeights[item._id];
            this.focusCappedIds.delete(item._id);
            this.clearFocusFlipStyles(item._id);
            this.unlockBodyScroll();
          }
          this.publishedItems = this.publishedItems.filter(
            (p) => p._id !== item._id
          );
          if (sourceId) {
            this.unsharedItems = this.unsharedItems.filter(
              (u) => u.id !== sourceId
            );
            this.unsharedSeenIds.add(sourceId);
            this.setUnsharedTabCount(this.unsharedItems.length);
            this.applyUnsharedRowsLayout(true);
          }
          this.worksTab = 'published';
          this.applyPublishedRowsLayout(true);
          this.fetchPublishedWorks(false, () => {
            setTimeout(() => this.setupUnsharedObserver(), 0);
          });
        },
        error: () => {
          item.savingTitle = false;
          delete this.unsharedShareSubs[sourceId || item._id];
          this.pendingHighlightKeys.clear();
          this.justAddedPreferImmediate = false;
          this.showSnack('wall.personal.publish-error');
        },
      });
  }

  togglePendingShareHidden(item: WallPublishedViewItem, event?: Event): void {
    event?.preventDefault();
    event?.stopPropagation();
    if (!item?.pendingShareFromUnshared || this.isPendingShareSubmitting(item)) {
      return;
    }
    item.hiddenFromAudience = !item.hiddenFromAudience;
  }

  onEditHideFromAudienceClick(item: WallPublishedViewItem, event?: Event): void {
    if (this.isPendingUnsharedShare(item)) {
      this.togglePendingShareHidden(item, event);
      return;
    }
    this.toggleHiddenFromAudience(item, event);
  }

  private buildPendingShareFromUnshared(
    item: WallUnsharedViewItem,
    pendingId: string
  ): WallPublishedViewItem {
    const thumb = String(item.thumbUrl || '');
    return {
      _id: pendingId,
      fileId: item.fileId,
      printingService: item.printingService,
      page: item.page,
      serialName: item.serialName,
      fileName: item.fileName || '',
      title: '',
      description: '',
      rotation: 0,
      thumbUrl: thumb,
      serverAddress: item.serverAddress || '',
      likeCount: 0,
      commentCount: 0,
      commenterCount: 0,
      likedByMe: false,
      recentLikers: [],
      recentCommenters: [],
      likers: [],
      likersExpanded: false,
      commentsOpen: false,
      commentsMounted: false,
      commentsLoading: false,
      comments: [],
      draftComment: '',
      liking: false,
      commenting: false,
      editingTitle: false,
      editingDescription: false,
      savingTitle: false,
      savingDescription: false,
      deleting: false,
      hiding: false,
      draftTitle: '',
      draftDescription: '',
      draftRotation: 0,
      baselineTitle: '',
      baselineDescription: '',
      baselineRotation: 0,
      hiddenFromAudience: false,
      workTitleExpanded: false,
      workDescExpanded: false,
      imgLoaded: !!item.imgLoaded,
      natW: item.natW,
      natH: item.natH,
      pendingShareFromUnshared: true,
      sourceUnsharedId: item.id,
    };
  }

  /**
   * User closed focus without confirming share — restore the unshared card.
   */
  private cancelPendingUnsharedShare(
    item: WallPublishedViewItem,
    opts?: { silent?: boolean }
  ): void {
    if (!item?.pendingShareFromUnshared) {
      return;
    }
    const sourceId = String(item.sourceUnsharedId || '');
    const source = sourceId
      ? this.pendingShareSourceById.get(sourceId)
      : undefined;
    this.pendingShareSourceById.delete(sourceId);
    this.publishedItems = this.publishedItems.filter((p) => p._id !== item._id);
    this.applyPublishedRowsLayout(true);
    const existing = sourceId
      ? this.unsharedItems.find((u) => u.id === sourceId)
      : undefined;
    if (existing) {
      existing.sharing = false;
      this.applyUnsharedRowsLayout(true);
    } else if (source) {
      this.unsharedItems = [{ ...source, sharing: false }, ...this.unsharedItems];
      this.setUnsharedTabCount(this.unsharedItems.length);
      this.applyUnsharedRowsLayout(true);
    }
    if (!opts?.silent) {
      this.worksTab = 'unshared';
    }
    this.cdr.markForCheck();
  }

  /** Open title/description editors and scroll the new card into view if needed. */
  private openAndRevealNewlySharedWorks(): void {
    if (!this.pendingRevealKeys.size) {
      return;
    }
    const matched = this.publishedItems.filter((item) =>
      this.pendingRevealKeys.has(`${item.fileId}:${item.page}`)
    );
    this.pendingRevealKeys.clear();
    if (!matched.length) {
      return;
    }

    this.publishedVisibleCount = Math.max(
      this.publishedVisibleCount,
      Math.min(this.publishedPageSize, this.publishedItems.length)
    );
    const matchedIds = new Set(matched.map((item) => String(item._id)));
    for (const other of this.publishedItems) {
      if (matchedIds.has(String(other._id))) {
        continue;
      }
      if (other.commentsOpen || other.commentsMounted) {
        other.commentsOpen = false;
        other.commentsMounted = false;
      }
      if (other.editingTitle || other.editingDescription) {
        other.editingTitle = false;
        other.editingDescription = false;
        other.draftTitle = other.baselineTitle;
        other.draftDescription = other.baselineDescription;
      }
    }
    this.applyPublishedRowsLayout(true);

    const first = matched[0];
    const firstId = String(first._id);
    setTimeout(() => {
      this.scrollPublishedCardIntoViewFast(firstId);
      this.openPublishedFocus(first, 'edit');
      this.scheduleEditFocusRelayout(first);
    }, 40);
  }

  private scrollPublishedCardIntoViewFast(itemId: string): void {
    if (!itemId || typeof window === 'undefined') {
      return;
    }
    const el = this.host.nativeElement.querySelector(
      `[data-published-id="${itemId}"]`
    ) as HTMLElement | null;
    if (!el) {
      return;
    }
    if (this.isElementMostlyInViewport(el)) {
      return;
    }

    const scroller = wallScrollParent(this.host.nativeElement);
    const elRect = el.getBoundingClientRect();
    const scrollerRect =
      scroller === document.documentElement || scroller === document.body
        ? { top: 0, height: window.innerHeight }
        : scroller.getBoundingClientRect();
    const currentTop =
      scroller === document.documentElement || scroller === document.body
        ? window.scrollY || document.documentElement.scrollTop
        : scroller.scrollTop;
    const elOffsetInScroller =
      elRect.top - scrollerRect.top + currentTop;
    const targetTop = Math.max(
      0,
      elOffsetInScroller - Math.max(24, scrollerRect.height * 0.12)
    );
    const delta = targetTop - currentTop;
    if (Math.abs(delta) < 12) {
      return;
    }

    if (this.revealScrollRaf != null) {
      cancelAnimationFrame(this.revealScrollRaf);
      this.revealScrollRaf = null;
    }

    const duration = Math.min(260, Math.max(140, Math.abs(delta) * 0.22));
    const start = performance.now();
    const easeOutCubic = (t: number) => 1 - Math.pow(1 - t, 3);
    const applyScroll = (value: number) => {
      if (scroller === document.documentElement || scroller === document.body) {
        window.scrollTo(0, value);
      } else {
        scroller.scrollTop = value;
      }
    };

    const tick = (now: number) => {
      const t = Math.min(1, (now - start) / duration);
      applyScroll(currentTop + delta * easeOutCubic(t));
      if (t < 1) {
        this.revealScrollRaf = requestAnimationFrame(tick);
      } else {
        this.revealScrollRaf = null;
      }
    };
    this.revealScrollRaf = requestAnimationFrame(tick);
  }

  private isElementMostlyInViewport(el: HTMLElement, margin = 20): boolean {
    const r = el.getBoundingClientRect();
    const vh = window.innerHeight || document.documentElement.clientHeight;
    return r.top >= margin && r.bottom <= vh - margin && r.height > 0;
  }

  onUnsharedImageLoad(event: Event, item: WallUnsharedViewItem): void {
    const img = event.target as HTMLImageElement;
    if (isWallPlaceholderThumb(img?.currentSrc || img?.src || item?.thumbUrl)) {
      this.removeUnsharedItem(item);
      return;
    }
    if (img?.naturalWidth && img.naturalHeight) {
      const prevW = item.natW;
      const prevH = item.natH;
      item.natW = img.naturalWidth;
      item.natH = img.naturalHeight;
      item.imgLoaded = true;
      if (wallDisplayedAspectChanged(prevW, prevH, item.natW, item.natH)) {
        this.scheduleBalancedRelayout();
      }
      return;
    }
    item.imgLoaded = true;
  }

  onUnsharedImageError(_event: Event, item: WallUnsharedViewItem): void {
    this.removeUnsharedItem(item);
  }

  private removeUnsharedItem(item: WallUnsharedViewItem): void {
    if (!item?.id) {
      return;
    }
    const before = this.unsharedItems.length;
    this.unsharedItems = this.unsharedItems.filter((u) => u.id !== item.id);
    if (this.unsharedItems.length !== before) {
      // Image failed — keep tab badge on last feed commit count.
      this.applyUnsharedRowsLayout(true);
    }
  }

  private lockStableScrollbarGutter(): void {
    if (typeof document === 'undefined') {
      return;
    }
    document
      .querySelector('mat-sidenav-content')
      ?.classList.add('wall-stable-scrollbar');
  }

  private unlockStableScrollbarGutter(): void {
    if (typeof document === 'undefined') {
      return;
    }
    document
      .querySelector('mat-sidenav-content')
      ?.classList.remove('wall-stable-scrollbar');
  }

  private readonly unsharedPrefetchMarginPx = (): number =>
    typeof window !== 'undefined' ? Math.round(window.innerHeight * 1.4) : 900;

  private setupUnsharedObserver(): void {
    this.unsharedObserver?.disconnect();
    const node = this.unsharedSentinelRef?.nativeElement;
    if (!node) {
      this.queueUnsharedCatchUp();
      return;
    }
    const margin = this.unsharedPrefetchMarginPx();
    this.unsharedObserver = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) {
          this.ngZone.run(() => this.loadUnsharedMore());
        }
      },
      { root: null, rootMargin: `${margin}px 0px`, threshold: 0 }
    );
    this.unsharedObserver.observe(node);
    this.queueUnsharedCatchUp();
  }

  private queueUnsharedCatchUp(): void {
    if (typeof window === 'undefined') {
      return;
    }
    if (this.unsharedCatchUpRaf != null) {
      cancelAnimationFrame(this.unsharedCatchUpRaf);
    }
    this.unsharedCatchUpRaf = window.requestAnimationFrame(() => {
      this.unsharedCatchUpRaf = window.requestAnimationFrame(() => {
        this.unsharedCatchUpRaf = null;
        this.ensureUnsharedCatchUp();
      });
    });
  }

  private ensureUnsharedCatchUp(): void {
    if (
      this.isLoadingUnshared ||
      this.isLoadingMoreUnshared ||
      !this.unsharedHasMore
    ) {
      return;
    }
    if (!this.isUnsharedPrefetchNearView()) {
      return;
    }
    this.loadUnsharedMore();
  }

  private isUnsharedPrefetchNearView(): boolean {
    if (typeof window === 'undefined') {
      return false;
    }
    const margin = this.unsharedPrefetchMarginPx();
    const sentinel = this.unsharedSentinelRef?.nativeElement;
    if (sentinel) {
      const r = sentinel.getBoundingClientRect();
      return r.bottom >= -margin && r.top <= window.innerHeight + margin;
    }
    const cards = this.host.nativeElement.querySelectorAll(
      '.personal-wall-card-unshared'
    );
    if (!cards.length) {
      return false;
    }
    const r = (cards[cards.length - 1] as HTMLElement).getBoundingClientRect();
    return r.bottom >= -margin && r.top <= window.innerHeight + margin;
  }

  private setupPublishedObserver(): void {
    this.publishedObserver?.disconnect();
    if (this.worksTab !== 'published') {
      return;
    }
    const node = this.publishedSentinelRef?.nativeElement;
    if (!node) {
      this.queuePublishedCatchUp();
      return;
    }
    const margin = this.unsharedPrefetchMarginPx();
    this.publishedObserver = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) {
          this.ngZone.run(() => this.loadPublishedMore());
        }
      },
      { root: null, rootMargin: `${margin}px 0px`, threshold: 0 }
    );
    this.publishedObserver.observe(node);
    this.queuePublishedCatchUp();
  }

  private queuePublishedCatchUp(): void {
    if (typeof window === 'undefined') {
      return;
    }
    if (this.publishedCatchUpRaf != null) {
      cancelAnimationFrame(this.publishedCatchUpRaf);
    }
    this.publishedCatchUpRaf = window.requestAnimationFrame(() => {
      this.publishedCatchUpRaf = window.requestAnimationFrame(() => {
        this.publishedCatchUpRaf = null;
        this.ensurePublishedCatchUp();
      });
    });
  }

  private ensurePublishedCatchUp(): void {
    if (this.worksTab !== 'published' || !this.publishedHasMore) {
      return;
    }
    if (!this.isPublishedPrefetchNearView()) {
      return;
    }
    this.loadPublishedMore();
  }

  private isPublishedPrefetchNearView(): boolean {
    if (typeof window === 'undefined') {
      return false;
    }
    const margin = this.unsharedPrefetchMarginPx();
    const sentinel = this.publishedSentinelRef?.nativeElement;
    if (sentinel) {
      const r = sentinel.getBoundingClientRect();
      return r.bottom >= -margin && r.top <= window.innerHeight + margin;
    }
    return false;
  }

  private compareCreatedDesc(
    a: { created?: string; _id?: string },
    b: { created?: string; _id?: string }
  ): number {
    const tb = new Date(b.created || 0).getTime();
    const ta = new Date(a.created || 0).getTime();
    if (tb !== ta) {
      return tb - ta;
    }
    return String(b._id || '').localeCompare(String(a._id || ''));
  }

  /** After images reveal real sizes, refresh row weights if comments aren't open. */
  private scheduleBalancedRelayout(): void {
    if (this.balanceRelayoutTimer) {
      clearTimeout(this.balanceRelayoutTimer);
    }
    this.balanceRelayoutTimer = setTimeout(() => {
      this.balanceRelayoutTimer = null;
      const busyPublished = this.publishedItems.some(
        (c) =>
          c.commentsOpen ||
          c.commentsMounted ||
          c.editingTitle ||
          c.editingDescription
      );
      if (!busyPublished && this.pagedPublishedItems.length) {
        this.applyPublishedRowsLayout(true);
      }
      if (this.unsharedItems.length) {
        this.applyUnsharedRowsLayout(true);
      }
      setTimeout(() => {
        this.queueUnsharedCatchUp();
        this.queuePublishedCatchUp();
      }, 0);
    }, 280);
  }

  private loadUserProfile(): void {
    const userId = this.authService.getUserId();
    if (!userId) {
      return;
    }
    this.userFetchSub = this.usersService.getUser(userId).subscribe({
      next: (user: any) => {
        this.applyUserProfile(user);
        this.recentPrintingServices = Array.isArray(user?.home_printingServices_list)
          ? user.home_printingServices_list
          : [];
        this.recentBranches = Array.isArray(user?.home_branches_list)
          ? user.home_branches_list
          : [];
        this.applySuggestedAcademy();
      },
      error: () => {
        this.profileImgCandidates = [];
        this.userProfileImg = null;
        this.recentPrintingServices = [];
        this.recentBranches = [];
        this.applySuggestedAcademy();
      },
    });
  }

  private applyUserProfile(user: any, forceName = false): void {
    if (!user) {
      return;
    }
    this.userEmail = user.email != null ? String(user.email) : this.userEmail;
    if (user.phone != null) {
      this.userPhone = user.phone;
    }
    const name = this.pickWallUserName(user);
    if (name && (forceName || !(this.editingName || this.isNameDirty() || this.savingProfile))) {
      this.userName = name;
      this.baselineName = name;
      this.draftName = name;
      this.profileNameLoaded = true;
    }
    const nextImgs = wallProfileImageCandidates(user);
    if (nextImgs.length) {
      this.profileImgCandidates = nextImgs;
      this.userProfileImg = nextImgs[0];
    } else if (!this.userProfileImg) {
      this.profileImgCandidates = [];
      this.userProfileImg = null;
    }
  }

  /**
   * Wall title: prefer displayName (profile name), never raw email.
   * Auth "userName" maps to username which is often the email.
   */
  private pickWallUserName(user: any): string {
    const displayName = String(user?.displayName || '').trim();
    if (displayName && !displayName.includes('@')) {
      return displayName;
    }
    const username = String(
      user?.username || user?.userName || ''
    ).trim();
    if (username && !username.includes('@')) {
      return username;
    }
    if (displayName) {
      return displayName;
    }
    return '';
  }

  private loadSessionFiles(): void {
    this.isLoading = true;
    const files = this.wallSessionService.getWallSessionFiles();
    this.sessionMeta = this.wallSessionService.getWallSessionMeta();
    this.sessionSignature = this.buildSessionSignature(files, this.sessionMeta);
    const mapped = this.mapSessionFilesToWallFiles(files, this.sessionMeta);
    this.ensureAcademyFromOrderSession();
    const hydrateGen = ++this.draftHydrateGen;
    const commit = () => {
      if (hydrateGen !== this.draftHydrateGen) {
        return;
      }
      this.wallFiles = mapped;
      this.isLoading = false;
      this.cdr.markForCheck();
      this.scheduleDraftMount();
      this.tryOpenPendingDirectShare();
    };
    if (!mapped.length) {
      commit();
      return;
    }
    hydrateWallItemImageSizes(mapped).then(() => this.ngZone.run(commit));
  }

  /** Write current draft UI (V / title / desc) back into the durable wall session. */
  private persistDraftSessionState(): void {
    if (!this.wallFiles.length) {
      return;
    }
    this.wallSessionService.patchDraftPages(
      this.wallFiles.map((f) => ({
        fileId: f.fileId,
        page: f.page,
        included: !!f.included,
        title: f.title || '',
        description: f.description || '',
        rotation: this.normalizeRotation(f.rotation),
        hiddenFromAudience: f.hiddenFromAudience === true,
      }))
    );
  }

  private buildSessionSignature(
    files: any[] | null | undefined,
    meta: WallSessionMeta | null
  ): string {
    const pages = (files || []).flatMap((f) => {
      const id = String(f?._id || f?.fileId || '');
      return (Array.isArray(f?.images) ? f.images : []).map(
        (img: any, i: number) =>
          `${id}:${img?.page || i + 1}:${img?.wallThumbUrl || img?.thumbnailPath || img?.path || ''}`
      );
    });
    return JSON.stringify({
      orderId: meta?.orderId || '',
      pages,
    });
  }

  private mapSessionFilesToWallFiles(
    files: any[],
    meta: WallSessionMeta | null
  ): WallFileItem[] {
    if (!files?.length) {
      return [];
    }
    const orderId = meta?.orderId || '';
    const items: WallFileItem[] = [];

    files.forEach((file, fileIndex) => {
      const fileId = String(file?._id || file?.fileId || '');
      if (!fileId) {
        return;
      }
      const images =
        Array.isArray(file?.images) && file.images.length ? file.images : [];
      if (!images.length) {
        return;
      }
      const pageCount = images.length;
      const fileName =
        file?.originalName ||
        file?.name ||
        file?.fileName ||
        `file-${fileIndex + 1}`;

      images.forEach((image: any, imgIndex: number) => {
        const pageNum = Number(image?.page);
        const page =
          Number.isFinite(pageNum) && pageNum >= 1 ? pageNum : imgIndex + 1;
        const thumbUrl = this.resolveSessionThumb(image, meta);
        if (isWallPlaceholderThumb(thumbUrl)) {
          return;
        }
        items.push({
          id: `${orderId || 'session'}_${fileId}_${page}`,
          fileId,
          orderId: String(orderId),
          fileName,
          title: image?.wallTitle != null ? String(image.wallTitle) : '',
          description:
            image?.wallDescription != null
              ? String(image.wallDescription)
              : '',
          thumbUrl,
          included: !!image?.wallIncluded,
          page,
          pageCount,
          rotation: this.normalizeRotation(image?.wallRotation),
          hiddenFromAudience: image?.wallHiddenFromAudience === true,
          imgLoaded: false,
        });
      });
    });

    return items;
  }

  private resolveSessionThumb(
    image: any,
    meta: WallSessionMeta | null
  ): string {
    const fallback = '';
    if (!image) {
      return fallback;
    }
    const thumbPath = String(image.thumbnailPath || '').trim();
    if (/^https?:\/\//i.test(thumbPath) && !isWallPlaceholderThumb(thumbPath)) {
      return thumbPath;
    }
    if (image.wallThumbUrl) {
      return isWallPlaceholderThumb(image.wallThumbUrl) ? '' : image.wallThumbUrl;
    }
    // Never use rotatedThumbnailPath — keep original orientation
    const path = image.thumbnailPath || image.path || image.imagePath || '';
    if (!path || String(path).toLowerCase().includes('rotated')) {
      const safePath = image.thumbnailPath || '';
      if (!safePath) {
        return fallback;
      }
      const url = this.buildSessionThumbUrl(safePath, meta) || fallback;
      return isWallPlaceholderThumb(url) ? '' : url;
    }
    const url = this.buildSessionThumbUrl(path, meta) || fallback;
    return isWallPlaceholderThumb(url) ? '' : url;
  }

  private buildSessionThumbUrl(
    path: string,
    meta: WallSessionMeta | null
  ): string {
    if (!path) {
      return '';
    }
    if (path.startsWith('/assets/') || path.startsWith('assets/')) {
      return path.startsWith('/') ? path : `/${path}`;
    }
    const fromOrdersRules = toPublicWallThumb(path, {
      serverAddress: meta?.serverAddress || '',
    });
    if (fromOrdersRules && fromOrdersRules !== path) {
      return fromOrdersRules;
    }
    if (/^https?:\/\//i.test(path) || fromOrdersRules) {
      return fromOrdersRules || path;
    }
    // Pending express (my-orders getImgExpress PENDING/QR/NOT_PAID branch)
    const fileName = path.split(/[/\\]/).pop() || '';
    const serial = String(meta?.serialName || '').trim();
    const userName = String(meta?.userName || '').trim();
    if (
      fileName &&
      serial &&
      userName &&
      String(meta?.printingService || '').toLowerCase() === 'express' &&
      !path.startsWith('/') &&
      !isWallPlaceholderThumb(fileName)
    ) {
      return `https://${serial}.eazix.io/uploads/${userName}/${fileName}`;
    }
    return path;
  }
}
